/* ==========================================================================
 * Clinician Routine Finder — Cosmetic Physician Partners
 * --------------------------------------------------------------------------
 * A dependency-free, client-side skincare consultation + recommendation
 * engine for Shopify Online Store 2.0.
 *
 * The engine consumes the JSON payload emitted by sections/skin-quiz.liquid.
 * CPP metafields (cpp.*) are the authoritative signal; product type/tags are
 * used only as a fallback.
 *
 * Module map (search these headers):
 *   [1] Utilities
 *   [2] Taxonomy & normalization   — quiz answers <-> messy metafield values
 *   [3] Ingredient knowledge base  — powers scoring + "why we chose it"
 *   [4] Question flow (data-driven)
 *   [5] State + persistence (auto-save / resume)
 *   [6] Recommendation engine      — score -> assemble -> rationale
 *   [7] UI renderer                — panels, transitions, results
 *   [8] Cart integration
 *   [9] Public API + extension hooks (AI selfie, Klaviyo, accounts, …)
 *   [10] Boot
 * ========================================================================== */
(function () {
  'use strict';

  /* ======================================================================
   * [1] UTILITIES
   * ==================================================================== */

  /** Normalize any label to a comparable token: lowercase, alphanumerics only.
   *  "Anti-Aging" / "Anti Aging"  -> "antiaging"
   *  "Dry/Dehydrated Skin" / "Dry Dehydrated Skin" -> "drydehydratedskin"      */
  function norm(v) {
    return String(v == null ? '' : v).toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  /** Coerce a metafield value that may be an array, JSON string, or scalar
   *  into a clean array of trimmed strings. Defensive against export quirks.   */
  function toArray(v) {
    if (v == null) return [];
    if (Array.isArray(v)) return v.map(function (x) { return String(x).trim(); }).filter(Boolean);
    var s = String(v).trim();
    if (!s) return [];
    // A stray JSON-looking string ("[\"Dry\",\"Oily\"]") — parse if possible.
    if (s[0] === '[') { try { var p = JSON.parse(s); if (Array.isArray(p)) return p.map(String); } catch (e) { /* fall through */ } }
    return s.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function normSet(arr) {
    var out = Object.create(null);
    toArray(arr).forEach(function (x) { out[norm(x)] = x; });
    return out;
  }

  /** Do two normalized-token collections intersect? */
  function intersects(setA, tokensB) {
    for (var i = 0; i < tokensB.length; i++) { if (setA[norm(tokensB[i])] != null) return true; }
    return false;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function elFromHTML(html) {
    var t = document.createElement('template');
    t.innerHTML = String(html).trim();
    return t.content.firstElementChild;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function debounce(fn, ms) {
    var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); };
  }

  function listToSentence(items) {
    items = items.filter(Boolean);
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return items[0] + ' and ' + items[1];
    return items.slice(0, -1).join(', ') + ', and ' + items[items.length - 1];
  }

  var ICONS = {
    chevron: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="m5 12 4 4L19 7"/></svg>',
    plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M12 5v14"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
    sun: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5.6 5.6 4.2 4.2M19.8 19.8l-1.4-1.4M18.4 5.6l1.4-1.4M4.2 19.8l1.4-1.4"/></svg>',
    moon: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/></svg>',
    spark: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18"/></svg>',
    grip: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>',
    up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m18 15-6-6-6 6"/></svg>',
    down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
    swap: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/></svg>',
    body: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><circle cx="12" cy="5" r="2.5"/><path d="M12 8v8M12 10l-5 3M12 10l5 3M9.5 22l2.5-6 2.5 6"/></svg>',
    hair: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M4 20c0-8 3.6-14 8-14s8 6 8 14"/><path d="M7 20c0-6 2.2-10 5-10s5 4 5 10"/></svg>',
    leaf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="M11 20A7 7 0 0 1 4 13c0-5 4-9 16-9 0 12-4 16-9 16Z"/><path d="M4 20c4-6 8-8 12-9"/></svg>'
  };

  /* ======================================================================
   * [2] TAXONOMY & NORMALIZATION
   *   Bridges quiz answers to the (inconsistent) cpp.* metafield vocabulary.
   * ==================================================================== */

  // Concern options. `tokens` are canonical cpp.concern spellings we accept
  // (matched via norm()); `ings` are ingredient-rule ids that address it.
  var CONCERNS = [
    { id: 'fine_lines',   label: 'Fine Lines',        tokens: ['Fine lines and wrinkles', 'Fine Lines', 'Wrinkles', 'Anti-Aging', 'Crows Feet', 'Lip Wrinkles'] },
    { id: 'wrinkles',     label: 'Wrinkles',          tokens: ['Wrinkles', 'Fine lines and wrinkles', 'Anti-Aging', 'Loss of Elasticity'] },
    { id: 'redness',      label: 'Redness',           tokens: ['Redness', 'Rosacea', 'Sensitive Skin', 'Inflammation', 'Sensitive'] },
    { id: 'acne',         label: 'Acne',              tokens: ['Acne', 'Acne & Blemishes', 'Congested Skin', 'Excess Sebum', 'Blackheads'] },
    { id: 'pigmentation', label: 'Pigmentation',      tokens: ['Discoloration', 'Hyperpigmentation', 'Uneven Skin Tone', 'Dark Spots', 'Melasma'] },
    { id: 'sun_damage',   label: 'Sun Damage',        tokens: ['Sun Damage', 'Free Radical Damage', 'Environmental Damage', 'Sunburn'] },
    { id: 'dryness',      label: 'Dryness',           tokens: ['Dry/Dehydrated Skin', 'Dry Dehydrated Skin', 'Dry Skin', 'Barrier Repair', 'Crepey Skin'] },
    { id: 'oiliness',     label: 'Oiliness',          tokens: ['Oily Skin', 'Oily', 'Excess Sebum', 'Congested Skin'] },
    { id: 'texture',      label: 'Texture',           tokens: ['Skin Texture', 'Uneven Texture', 'Dullness', 'Congested Skin'] },
    { id: 'pores',        label: 'Pores',             tokens: ['Large Pores', 'Congested Skin', 'Excess Sebum', 'Blackheads'] },
    { id: 'firmness',     label: 'Loss of Firmness',  tokens: ['Loss of Firmness', 'Loss of Elasticity', 'Sagging Skin', 'Volume', 'Anti-Aging'] },
    // Dark circles are an eye-area concern — flagged so they only steer the eye
    // step, never inflating cleanser / SPF / moisturizer selection.
    { id: 'dark_circles', label: 'Dark Circles',      tokens: ['Dark Circles', 'Under Eye Circles', 'Puffiness'], eyeOnly: true }
  ];
  var CONCERN_BY_ID = {};
  CONCERNS.forEach(function (c) { CONCERN_BY_ID[c.id] = c; });

  // Skin types. `compat` = canonical cpp.skintype values considered a match.
  var SKIN_TYPES = [
    { id: 'dry',         label: 'Dry',               desc: 'Tight, flaky, or rough',      compat: ['Dry', 'Normal to Dry', 'All Skin Types', 'Mature', 'Sensitive'] },
    { id: 'oily',        label: 'Oily',              desc: 'Shine and visible pores',     compat: ['Oily', 'Normal to Oily', 'All Skin Types', 'Acne Prone', 'Combination'] },
    { id: 'combination', label: 'Combination',       desc: 'Oily T-zone, drier cheeks',   compat: ['Combination', 'Normal to Oily', 'Normal to Dry', 'All Skin Types', 'Normal'] },
    { id: 'normal',      label: 'Normal / Balanced', desc: 'Comfortable, few concerns',   compat: ['Normal', 'All Skin Types', 'Normal to Dry', 'Normal to Oily', 'Combination'] }
  ];
  var SKIN_BY_ID = {};
  SKIN_TYPES.forEach(function (s) { SKIN_BY_ID[s.id] = s; });

  // Routine steps. `types` are normalized cpp.producttype tokens; a product
  // qualifies for the step if any of its producttypes normalizes into the set.
  var STEPS = {
    cleanser:    { key: 'cleanser',    label: 'Cleanser',    types: ['cleanser', 'cleansers', 'facialcleanser', 'facialcleansers', 'facewash'] },
    treatment:   { key: 'treatment',   label: 'Treatment',   types: ['faceserum', 'faceserums', 'facetreatment', 'facetreatments', 'facialserum', 'acnetreatment', 'acnetreatments', 'darkspotcorrector', 'darkspotcorrectors', 'facialtreatment', 'toner', 'toners'] },
    moisturizer: { key: 'moisturizer', label: 'Moisturizer', types: ['facialmoisturizer', 'facialmoisturizers', 'facecream', 'facecreams', 'faciallotion', 'faciallotions', 'moisturizer', 'moisturizers', 'gelcream', 'neckcream', 'neckcreams'] },
    spf:         { key: 'spf',         label: 'SPF',         types: ['sunscreen', 'sunscreens'] },
    eye:         { key: 'eye',         label: 'Eye',         types: ['eyecream', 'eyecreams', 'eyeserum', 'eyeserums', 'eyetreatment', 'eyetreatments'] },
    lip:         { key: 'lip',         label: 'Lip',         types: ['liptreatment', 'liptreatments', 'lipbalm', 'lipbalms'] },
    exfoliant:   { key: 'exfoliant',   label: 'Exfoliant',   types: ['faceexfoliant', 'faceexfoliants', 'facemask', 'facemasks', 'facialmask'] }
  };

  // Face-relevant application areas (normalized). Products whose ONLY areas are
  // off-face (body/hands/feet/legs/scalp) are excluded from the facial routine.
  var FACE_AREAS = ['face', 'neckdecolette', 'neck', 'eyes', 'lips'];
  var OFFACE_AREAS = ['body', 'hands', 'feet', 'legs', 'scalp'];

  // Core steps (vs. the specialized eye/lip/exfoliant steps). A product that is
  // primarily an eye/lip product is de-prioritized when filling a core step.
  var CORE_STEPS = { cleanser: 1, treatment: 1, moisturizer: 1, spf: 1 };

  /** Which routine step(s) a set of normalized product types can serve. */
  function stepsForNorm(ptNorm) {
    var out = [];
    Object.keys(STEPS).forEach(function (key) {
      if (ptNorm.some(function (t) { return STEPS[key].types.indexOf(t) !== -1; })) out.push(key);
    });
    return out;
  }

  /* ======================================================================
   * [2b] BRANCHES  (Skin · Body · Hair · Wellness)
   *   Each product is assigned a branch — first from the SKU pre-seed map
   *   (assets/skin-quiz-catalog.js, identical across clinics), then from live
   *   Category / product-type classification. The quiz runs ONE branch per
   *   session; Body/Hair/Wellness use short tailored flows + a generic
   *   step-based assembler (Skin keeps its richer engine).
   * ==================================================================== */
  var BRANCHES = {
    skin:     { id: 'skin',     label: 'Skin',        desc: 'A facial routine built around your concerns' },
    body:     { id: 'body',     label: 'Body',        desc: 'Head-to-toe body skin, from dryness to firming' },
    hair:     { id: 'hair',     label: 'Hair & Scalp', desc: 'Growth, shedding, scalp health and strength' },
    wellness: { id: 'wellness', label: 'Wellness',    desc: 'Physician-grade supplements for your goals' }
  };

  // Live branch classification (fallback when a SKU isn't in the pre-seed map).
  // Mirrors the generator: Category is authoritative, then keyword heuristics.
  function skinOrBody(aa) {
    return (aa.indexOf('body') !== -1 && aa.indexOf('face') === -1 && aa.indexOf('neck') === -1) ? 'body' : 'skin';
  }
  // Live branch classification. PRODUCT-TYPE driven, not title-keyword driven —
  // words like "vitamin"/"mineral" appear in skincare names, so we never
  // classify on those alone. Category is authoritative when present. This runs
  // only for the small share of products whose SKU isn't in the pre-seed map.
  function classifyBranchLive(category, ptNorm, aaNorm, title) {
    var cat = String(category || '');
    var aa = (aaNorm || []).join(' ');
    var titleN = norm(title || '');
    // 1) Category full taxonomy path (authoritative).
    if (/Vitamins & Supplements|Fitness & Nutrition/.test(cat)) return 'wellness';
    if (/Hair Care/.test(cat)) return 'hair';
    if (/Bath & Body/.test(cat)) return 'body';
    if (/Makeup|Cosmetic Tools|Cosmetic Sets/.test(cat)) return 'makeup';
    if (/Skin Care/.test(cat)) return skinOrBody(aa);
    // 2) Product-type / leaf-category signals (blob excludes free-text title).
    var typed = norm(cat + ' ' + (ptNorm || []).join(' '));
    if (/supplement|probiotic|capsule|nutraceutical|creatine|digestive|multivitamin|aminoacid|singlevitamin|\bminerals\b|\bvitamins\b|herbalsupplement/.test(typed)) return 'wellness';
    if (/shampoo|conditioner|scalp|hairserum|hairtreatment|hairloss|lashbrow|eyebrowserum|eyelashserum|hairsupplement|haircare/.test(typed)) return 'hair';
    if (/bodywash|bodylotion|bodycream|bodytreatment|bodyexfoliant|bodyoil|bodymask|bodysunscreen|handcream|bathbody/.test(typed)) return 'body';
    if (/cleanser|serum|moistur|sunscreen|eyecream|toner|exfoliant|facecream|facemask|liptreatment|lipbalm|facetreatment|peel/.test(typed)) return skinOrBody(aa);
    // 3) Last resort — supplement/hair/body-SPECIFIC title words only.
    if (/supplement|probiotic|capsule|nutraceutical|gummies/.test(titleN)) return 'wellness';
    if (/shampoo|conditioner|scalp|\bhair\b/.test(titleN)) return 'hair';
    if (/\bbody\b/.test(titleN)) return 'body';
    return skinOrBody(aa);
  }

  // Step definitions per non-skin branch. `types` = normalized cpp.producttype
  // tokens; slots = how many to recommend for that step.
  var BRANCH_STEPS = {
    body: [
      { key: 'cleanse',    label: 'Cleanse',   types: ['bodywash', 'bodycleanser', 'bodywashes', 'cleanser', 'cleansers'], slots: 1, optional: true },
      { key: 'treat',      label: 'Treat',     types: ['bodytreatment', 'bodytreatments', 'bodyexfoliant', 'bodyexfoliants', 'bodyserum', 'bodyoil', 'bodyoils', 'bodymask', 'bodymasks'], slots: 2 },
      { key: 'moisturize', label: 'Moisturize', types: ['bodylotion', 'bodylotions', 'bodycream', 'bodycreams', 'bodymoisturizer', 'handcream', 'handcreams', 'bodyoil', 'bodyoils', 'faciallotion'], slots: 1 },
      { key: 'protect',    label: 'Protect',   types: ['bodysunscreen', 'bodysunscreens', 'sunscreen', 'sunscreens'], slots: 1, optional: true }
    ],
    hair: [
      { key: 'cleanse',   label: 'Cleanse',   types: ['shampoo', 'shampoos', 'haircleanser', 'scalptreatment', 'scalptreatments'], slots: 1, optional: true },
      { key: 'condition', label: 'Condition', types: ['conditioner', 'conditioners'], slots: 1, optional: true },
      { key: 'treat',     label: 'Treat',     types: ['hairserum', 'hairserums', 'hairtreatment', 'hairtreatments', 'hairlosstreatment', 'hairlosstreatments', 'scalptreatment', 'scalptreatments', 'eyebrowserum', 'eyelashserum', 'lashbrowserum'], slots: 2 },
      { key: 'support',   label: 'Support',   types: ['hairsupplement', 'hairsupplements', 'dietarysupplement', 'supplement', 'supplements'], slots: 1, optional: true }
    ],
    wellness: [
      // The whole branch is supplements, so every wellness product is eligible
      // for the single stack (matchAll) — supplement metafields are often sparse.
      { key: 'stack', label: 'Recommended', matchAll: true, types: ['dietarysupplement', 'supplement', 'herbalsupplement', 'vitaminsandsupplements', 'minerals', 'hairsupplement', 'probiotics', 'digestivesupplements', 'multivitaminsupplements', 'singlevitamins', 'aminoacids', 'collagen', 'creatine'], slots: 4 }
    ]
  };

  // Concern / goal options per non-skin branch. `tokens` match cpp.concern;
  // `kw` are keyword hints matched against title + category + producttype
  // (important for Wellness, where cpp.concern is sparse).
  var BRANCH_CONCERNS = {
    body: [
      { id: 'body_dry',    label: 'Dryness',            tokens: ['Dry/Dehydrated Skin', 'Dry Skin', 'Barrier Repair', 'Cracked Skin', 'Itchy Skin'], kw: ['hydrat', 'moistur', 'nourish'] },
      { id: 'body_firm',   label: 'Crepey / Firmness',  tokens: ['Loss of Firmness', 'Crepey Skin', 'Loss of Elasticity', 'Sagging Skin', 'Anti-Aging'], kw: ['firm', 'tighten', 'trihex'] },
      { id: 'body_texture',label: 'Bumps / Texture',    tokens: ['Skin Texture', 'Razor Bumps', 'Folliculitis', 'Uneven Texture', 'Congested Skin'], kw: ['exfoliat', 'smooth', 'kp'] },
      { id: 'body_tone',   label: 'Tone / Dark Spots',  tokens: ['Discoloration', 'Hyperpigmentation', 'Uneven Skin Tone', 'Dark Spots', 'Sun Damage'], kw: ['brighten', 'even tone'] },
      { id: 'body_sun',    label: 'Sun Protection',     tokens: ['Sun Damage', 'Sunburn', 'Free Radical Damage'], kw: ['spf', 'sunscreen', 'protect'] }
    ],
    hair: [
      { id: 'hair_thinning', label: 'Thinning / Hair Loss', tokens: ['Thinning Hair', 'Hair', 'Hair Loss'], kw: ['growth', 'thicken', 'density', 'follicle', 'minoxidil', 'nutrafol', 'boosting'] },
      { id: 'hair_shedding', label: 'Shedding',             tokens: ['Hair Shedding'], kw: ['shed', 'strength', 'anchor'] },
      { id: 'hair_scalp',    label: 'Scalp Health',         tokens: ['Scalp', 'Seborrheic Dermatitis', 'Dandruff', 'Folliculitis'], kw: ['scalp', 'clarify', 'balance', 'dandruff'] },
      { id: 'hair_dry',      label: 'Dryness / Damage',     tokens: ['Dry/Dehydrated Skin', 'Barrier Repair', 'Damage'], kw: ['repair', 'hydrat', 'nourish', 'moistur', 'condition'] }
    ],
    wellness: [
      { id: 'well_skin',   label: 'Skin Support',       tokens: ['Anti-Aging', 'Acne', 'Dullness', 'Sun Damage'], kw: ['skin', 'clear skin', 'collagen', 'glow', 'complexion', 'radiance', 'nutraceutical', 'sun', 'armor', 'antioxidant', 'polypodium', 'ceramide'] },
      { id: 'well_hair',   label: 'Hair Support',       tokens: ['Thinning Hair', 'Hair'], kw: ['hair', 'growth', 'biotin', 'nutrafol', 'keratin', 'vegan'] },
      { id: 'well_gut',    label: 'Digestion / Gut',    tokens: [], kw: ['gut', 'digest', 'probiotic', 'biotic', 'enzyme', 'bile', 'pancreatin', 'mucosa', 'spore', 'fiber', 'prebiotic'] },
      { id: 'well_energy', label: 'Energy / Immunity',  tokens: [], kw: ['energy', 'immune', 'immunity', 'vitamin d', 'vitamin c', 'b12', 'zinc', 'iron', 'mitochond', 'focus', 'epa', 'omega', 'coq10', 'red yeast', 'electrolyte', 'creatine', 'nad'] },
      { id: 'well_balance',label: 'Hormone / Balance',  tokens: [], kw: ['hormone', 'balance', 'women', 'men', 'menopause', 'dhea', 'estrogen', 'thyroid', 'adrenal', 'cortisol', 'vegan'] },
      { id: 'well_calm',   label: 'Sleep / Stress',     tokens: [], kw: ['sleep', 'stress', 'calm', 'ashwagandha', 'magnesium', 'melatonin', 'relax', 'mood', 'brain', 'theanine', 'memory', 'sleepgevity'] },
      { id: 'well_general',label: 'General Wellness',   tokens: [], kw: ['multivitamin', 'daily', 'wellness', 'omega', 'fish oil', 'essential', 'complete', 'electrolyte'] }
    ]
  };
  var BRANCH_CONCERN_BY_ID = {};
  Object.keys(BRANCH_CONCERNS).forEach(function (b) {
    BRANCH_CONCERNS[b].forEach(function (c) { BRANCH_CONCERN_BY_ID[c.id] = c; });
  });

  /* ======================================================================
   * [3] INGREDIENT KNOWLEDGE BASE
   *   Each rule: keyword substrings (normalized) -> concern ids it targets
   *   + a clinician-style blurb used in "Why we chose it".
   *   `strong: true` marks potent actives (retinoids / exfoliating acids) that
   *   we gate for sensitive skin and beginners.
   * ==================================================================== */
  var INGREDIENT_RULES = [
    { id: 'retinoid',   kw: ['retinol', 'retinal', 'retinoid', 'vitamina', 'tretinoin', 'alltransretinol', 'alpharet'], targets: ['fine_lines', 'wrinkles', 'texture', 'pores', 'firmness'], blurb: 'vitamin A (retinol) to smooth fine lines and refine texture', strong: true },
    { id: 'bakuchiol',  kw: ['bakuchiol'], targets: ['fine_lines', 'wrinkles', 'texture', 'redness'], blurb: 'bakuchiol, a gentle retinol alternative' },
    { id: 'vitc',       kw: ['vitaminc', 'vitamicc', 'ascorbic', 'ascorbate', 'chromabright', 'kakadu'], targets: ['pigmentation', 'sun_damage', 'texture'], blurb: 'vitamin C to brighten and even tone' },
    { id: 'niacinamide',kw: ['niacinamide', 'nicotinamide', 'vitaminb3', 'benzoylnicotinate'], targets: ['redness', 'pores', 'oiliness', 'pigmentation'], blurb: 'niacinamide to calm redness and refine pores' },
    { id: 'ha',         kw: ['hyaluronic', 'sodiumhyaluronate'], targets: ['dryness'], blurb: 'hyaluronic acid for lasting hydration' },
    { id: 'barrier',    kw: ['squalane', 'ceramide', 'glycerin', 'sheabutter', 'biolipid', 'panthenol', 'lipids', 'urea'], targets: ['dryness'], blurb: 'barrier lipids to restore and hold hydration' },
    { id: 'bha',        kw: ['salicylic'], targets: ['acne', 'pores', 'oiliness', 'texture'], blurb: 'salicylic acid to clear pores and excess oil', strong: true },
    { id: 'aha',        kw: ['glycolic', 'lacticacid', 'mandelic', 'hydroxyacid', 'ahaandbeta'], targets: ['texture', 'pigmentation', 'pores'], blurb: 'exfoliating acids to renew texture and tone', strong: true },
    { id: 'peptides',   kw: ['peptide', 'palmitoyl', 'tetrapeptide', 'tripeptide', 'hexapeptide', 'ghkcu', 'copperpeptide', 'trihex'], targets: ['firmness', 'fine_lines', 'wrinkles'], blurb: 'peptides to support firmness and collagen' },
    { id: 'growth',     kw: ['growthfactor', 'exosome', 'humanplatelet', 'vitacell', 'ifirm'], targets: ['firmness', 'wrinkles', 'sun_damage'], blurb: 'growth-factor technology to support skin renewal' },
    { id: 'mineral',    kw: ['zincoxide', 'titaniumdioxide', 'mineral'], targets: ['sun_damage'], blurb: 'mineral filters for broad-spectrum protection' },
    { id: 'soothe',     kw: ['bisabolol', 'centella', 'greentea', 'allantoin', 'aloe', 'thermalspringwater', 'arnica', 'chamomile', 'oat', 'madecassoside'], targets: ['redness'], blurb: 'soothing botanicals to calm and comfort' },
    { id: 'caffeine',   kw: ['caffeine'], targets: ['dark_circles'], blurb: 'caffeine to de-puff and brighten the eye area' },
    { id: 'antiox',     kw: ['antioxidant', 'vitamine', 'tocopherol', 'resveratrol', 'coenzymeq10', 'lipochroman', 'ectoin', 'polypodium', 'coq10'], targets: ['sun_damage', 'texture'], blurb: 'antioxidants to defend against environmental stress' },
    { id: 'brighten',   kw: ['tranexamic', 'arbutin', 'hexylresorcinol', 'licorice', 'cysteamine', 'kojic', 'mandelicacid'], targets: ['pigmentation'], blurb: 'targeted brighteners to fade discoloration' },
    { id: 'collagen',   kw: ['collagen', 'marinecollagen'], targets: ['firmness'], blurb: 'collagen support for firmness and bounce' }
  ];

  /** Match a product's key ingredients against the knowledge base.
   *  Returns { rules:[rule], byConcern:{concernId:[rule]}, hasStrong:bool }.   */
  function analyzeIngredients(keyIngredients) {
    var rules = [], byConcern = {}, seen = {}, hasStrong = false;
    toArray(keyIngredients).forEach(function (ing) {
      var n = norm(ing);
      INGREDIENT_RULES.forEach(function (rule) {
        var hit = rule.kw.some(function (k) { return n.indexOf(k) !== -1; });
        if (!hit || seen[rule.id]) return;
        seen[rule.id] = true;
        rules.push(rule);
        if (rule.strong) hasStrong = true;
        rule.targets.forEach(function (t) { (byConcern[t] = byConcern[t] || []).push(rule); });
      });
    });
    return { rules: rules, byConcern: byConcern, hasStrong: hasStrong };
  }

  /* ======================================================================
   * [4] QUESTION FLOW (data-driven)
   *   Adding / reordering a question is a data edit. Some option sets are
   *   generated from the live catalogue (concerns, preferences).
   * ==================================================================== */

  // Base flow: lead card (optional) + branch picker. The branch's own
  // questions are appended once the shopper chooses a branch (rebuildFlow).
  function buildBaseFlow(catalogue, cfg) {
    var steps = [];
    if (cfg.lead && cfg.lead.enabled) {
      steps.push({
        id: 'contact', group: cfg.lead.eyebrow || 'Your consultation', type: 'contact',
        title: cfg.lead.heading || 'First, a little about you',
        subtitle: cfg.lead.subheading || "We'll save your routine and connect you with your preferred clinic.",
        locations: cfg.lead.locations || [],
        requirePhone: !!cfg.lead.requirePhone,
        genderLabel: (cfg.lead.gender && cfg.lead.gender.label) || '',
        genderOptions: (cfg.lead.gender && cfg.lead.gender.options) || [],
        genderRequired: !!(cfg.lead.gender && cfg.lead.gender.required),
        consentLabel: cfg.lead.consentLabel || '',
        consentDefault: !!cfg.lead.consentDefault
      });
    }
    // Branch picker — only offer branches that actually have products in-store.
    var present = (catalogue && catalogue.branches) || {};
    var branchOpts = Object.keys(BRANCHES)
      .filter(function (b) { return (present[b] || 0) > 0; })
      .map(function (b) { return { value: b, label: BRANCHES[b].label, desc: BRANCHES[b].desc }; });
    if (branchOpts.length > 1) {
      steps.push({
        id: 'branch', group: 'Get started', type: 'single', rebuild: true,
        title: 'What can we help you with?',
        subtitle: "Pick an area and we'll tailor the whole consultation to it.",
        options: branchOpts
      });
    }
    return steps;
  }

  // The full flow = base + the selected branch's questions.
  function buildFlow(catalogue, cfg, branch) {
    var steps = buildBaseFlow(catalogue, cfg);
    // If only one branch exists (or none), default to it silently.
    if (!branch) {
      var present = (catalogue && catalogue.branches) || {};
      var only = Object.keys(BRANCHES).filter(function (b) { return (present[b] || 0) > 0; });
      if (only.length === 1) branch = only[0];
    }
    if (branch === 'body') return steps.concat(bodyFlow(catalogue, cfg));
    if (branch === 'hair') return steps.concat(hairFlow(catalogue, cfg));
    if (branch === 'wellness') return steps.concat(wellnessFlow(catalogue, cfg));
    if (branch === 'skin') return steps.concat(skinFlow(catalogue, cfg));
    return steps; // no branch chosen yet
  }

  // ---- Skin (full flow) -------------------------------------------------
  function skinFlow(catalogue, cfg) {
    return [
      {
        id: 'age', group: 'Skin Profile', type: 'single',
        title: "What's your age range?",
        subtitle: 'This helps us calibrate ingredient strength and focus.',
        options: [
          { value: 'under25', label: 'Under 25', desc: 'Prevention & healthy habits' },
          { value: '25_34',   label: '25–34',    desc: 'Early prevention' },
          { value: '35_44',   label: '35–44',    desc: 'Targeted correction' },
          { value: '45_54',   label: '45–54',    desc: 'Firming & renewal' },
          { value: '55plus',  label: '55+',      desc: 'Restore & replenish' }
        ]
      },
      {
        id: 'skintype', group: 'Skin Profile', type: 'single',
        title: 'How would you describe your skin?',
        subtitle: 'Think about how your skin feels a few hours after cleansing.',
        options: SKIN_TYPES.map(function (s) { return { value: s.id, label: s.label, desc: s.desc }; })
      },
      {
        id: 'sensitivity', group: 'Skin Profile', type: 'single',
        title: 'How sensitive is your skin?',
        subtitle: 'We use this to balance efficacy with tolerance.',
        options: [
          { value: 'low',  label: 'Not sensitive',       desc: 'Rarely reacts to products' },
          { value: 'some', label: 'Somewhat sensitive',  desc: 'Occasional redness or stinging' },
          { value: 'high', label: 'Very sensitive',      desc: 'Reacts easily, prone to redness' }
        ]
      },
      {
        id: 'experience', group: 'Skin Profile', type: 'single',
        title: "What's your experience with skincare?",
        subtitle: 'So we can pace your actives appropriately.',
        options: [
          { value: 'new',      label: 'New to it',   desc: 'Keep it simple and gentle' },
          { value: 'some',     label: 'Comfortable', desc: 'I have an established routine' },
          { value: 'advanced', label: 'Advanced',    desc: 'I use actives and want results' }
        ]
      },
      {
        id: 'concerns', group: 'Skin Concerns', type: 'multi', min: 1, max: cfg.maxConcerns,
        title: 'What would you like to focus on?',
        subtitle: 'Choose up to ' + cfg.maxConcerns + '. We build your routine around these.',
        options: CONCERNS.map(function (c) { return { value: c.id, label: c.label }; })
      },
      {
        id: 'priority', group: 'Skin Concerns', type: 'rank', min: 1,
        title: 'Which matters most?',
        subtitle: 'Drag to rank, or use the arrows. Your top priority carries the most weight.',
        dynamic: true // options derived from `concerns`
      },
      {
        id: 'spf_daily', group: 'Lifestyle', type: 'single',
        title: 'Do you wear SPF daily?',
        subtitle: "Daily protection is the single highest-impact step — we'll include it either way.",
        options: [
          { value: 'yes',       label: 'Every day',  desc: 'Great — we\'ll keep you covered' },
          { value: 'sometimes', label: 'Sometimes',  desc: 'We\'ll make it effortless' },
          { value: 'no',        label: 'Not yet',    desc: 'We\'ll help you start' }
        ]
      },
      {
        id: 'retinol', group: 'Lifestyle', type: 'single',
        title: 'Have you used retinol before?',
        subtitle: 'Retinoids are powerful — tolerance guides how we introduce them.',
        options: [
          { value: 'never',      label: 'Never',      desc: 'We\'ll start gentle or skip it' },
          { value: 'previously', label: 'In the past', desc: 'We\'ll ease back in' },
          { value: 'currently',  label: 'Currently',  desc: 'We\'ll keep the momentum' }
        ]
      },
      {
        id: 'outdoor', group: 'Lifestyle', type: 'single',
        title: "What's your time outdoors like?",
        subtitle: 'This shapes your sun-protection and antioxidant needs.',
        options: [
          { value: 'minimal', label: 'Mostly indoors', desc: 'Lower daily exposure' },
          { value: 'average', label: 'A mix',          desc: 'Typical daily exposure' },
          { value: 'high',    label: 'Often outdoors', desc: 'Higher, more active exposure' }
        ]
      },
      {
        id: 'preferences', group: 'Preferences', type: 'multi', min: 0, optional: true,
        title: 'Any formulation preferences?',
        subtitle: 'Optional — we\'ll honour these wherever possible. Skip if none apply.',
        options: buildPreferenceOptions(catalogue)
      }
    ];
  }

  // ---- Body (short tailored flow) --------------------------------------
  function bodyFlow(catalogue, cfg) {
    return [
      {
        id: 'body_concerns', group: 'Body', type: 'multi', min: 1, max: 3,
        title: 'What are we treating?',
        subtitle: 'Choose up to 3. We\'ll build a head-to-toe body routine around these.',
        options: BRANCH_CONCERNS.body.map(function (c) { return { value: c.id, label: c.label }; })
      },
      {
        id: 'body_sun', group: 'Body', type: 'single',
        title: 'How much sun does your body get?',
        subtitle: 'Guides whether we prioritise body SPF.',
        options: [
          { value: 'high',    label: 'Often exposed', desc: 'Beach, outdoor activity, shorts weather' },
          { value: 'average', label: 'Sometimes',     desc: 'Typical day-to-day' },
          { value: 'low',     label: 'Rarely',        desc: 'Mostly covered up' }
        ]
      },
      {
        id: 'preferences', group: 'Preferences', type: 'multi', min: 0, optional: true,
        title: 'Any formulation preferences?',
        subtitle: 'Optional — we\'ll honour these wherever possible. Skip if none apply.',
        options: buildPreferenceOptions(catalogue)
      }
    ];
  }

  // ---- Hair & scalp (short tailored flow) ------------------------------
  function hairFlow(catalogue, cfg) {
    return [
      {
        id: 'hair_goal', group: 'Hair & Scalp', type: 'multi', min: 1, max: 2,
        title: 'What\'s your main hair goal?',
        subtitle: 'Choose up to 2.',
        options: BRANCH_CONCERNS.hair.map(function (c) { return { value: c.id, label: c.label }; })
      },
      {
        id: 'hair_scalp', group: 'Hair & Scalp', type: 'single',
        title: 'How would you describe your scalp?',
        subtitle: 'Helps us balance cleansing and nourishment.',
        options: [
          { value: 'balanced', label: 'Balanced',  desc: 'Comfortable, few issues' },
          { value: 'dry',      label: 'Dry / flaky', desc: 'Tight, itchy, or flaky' },
          { value: 'oily',     label: 'Oily',      desc: 'Greasy quickly' },
          { value: 'sensitive',label: 'Sensitive', desc: 'Reacts or gets irritated' }
        ]
      },
      {
        id: 'hair_supplements', group: 'Hair & Scalp', type: 'single',
        title: 'Open to a growth supplement?',
        subtitle: 'Physician-grade support taken alongside your topicals.',
        options: [
          { value: 'yes', label: 'Yes, include it', desc: 'Recommend one if it fits' },
          { value: 'no',  label: 'Topicals only',   desc: 'Skip supplements' }
        ]
      }
    ];
  }

  // ---- Wellness (goal-based supplement flow) ---------------------------
  function wellnessFlow(catalogue, cfg) {
    return [
      {
        id: 'wellness_goals', group: 'Wellness', type: 'multi', min: 1, max: 3,
        title: 'What are your wellness goals?',
        subtitle: 'Choose up to 3 and we\'ll match physician-grade supplements.',
        options: BRANCH_CONCERNS.wellness.map(function (c) { return { value: c.id, label: c.label }; })
      },
      {
        id: 'wellness_diet', group: 'Wellness', type: 'multi', min: 0, optional: true,
        title: 'Any dietary preferences?',
        subtitle: 'Optional — we\'ll honour these where possible.',
        options: [
          { value: 'vegan',        label: 'Vegan',        kind: 'pref', match: 'Vegan' },
          { value: 'gluten_free',  label: 'Gluten Free',  kind: 'pref', match: 'Gluten Free' },
          { value: 'non_gmo',      label: 'Non-GMO',      kind: 'pref', match: 'Non-GMO' },
          { value: 'hormone_free', label: 'Hormone Free', kind: 'pref', match: 'Hormone Free' }
        ]
      }
    ];
  }

  // Preference options: a curated priority set + the most common real
  // cpp.preferences values found in the catalogue + two behavioural toggles.
  // `kind` tells the engine how to apply each: pref | sunscreentype | shade | behavior
  function buildPreferenceOptions(catalogue) {
    var priority = [
      { value: 'fragrance_free', label: 'Fragrance Free',  kind: 'pref',          match: 'Fragrance Free' },
      { value: 'mineral_spf',    label: 'Mineral SPF',      kind: 'sunscreentype', match: 'Mineral/Physical' },
      { value: 'tinted_spf',     label: 'Tinted SPF',       kind: 'shade',         match: 'Tinted' },
      { value: 'minimal',        label: 'Minimal Routine',  kind: 'behavior' },
      { value: 'maximum',        label: 'Maximum Results',  kind: 'behavior' },
      { value: 'cruelty_free',   label: 'Cruelty Free',     kind: 'pref',          match: 'Cruelty Free' },
      { value: 'vegan',          label: 'Vegan',            kind: 'pref',          match: 'Vegan' },
      { value: 'oil_free',       label: 'Oil Free',         kind: 'pref',          match: 'Oil Free' },
      { value: 'non_comedogenic',label: 'Non-Comedogenic',  kind: 'pref',          match: 'Non-Comedogenic' },
      { value: 'paraben_free',   label: 'Paraben Free',     kind: 'pref',          match: 'Paraben Free' },
      { value: 'alcohol_free',   label: 'Alcohol Free',     kind: 'pref',          match: 'Alcohol Free' }
    ];
    // Keep only preference-backed options that actually exist in the catalogue
    // (behavioural + SPF specials always shown). Order preserved.
    var available = catalogue.prefValueSet;
    return priority.filter(function (o) {
      if (o.kind === 'behavior' || o.kind === 'sunscreentype' || o.kind === 'shade') return true;
      return available[norm(o.match)] != null;
    });
  }

  /* ======================================================================
   * [5] STATE + PERSISTENCE
   * ==================================================================== */
  function createStore(key) {
    var state = { answers: {}, index: 0, started: false, completed: false };
    try {
      var raw = localStorage.getItem(key);
      if (raw) { var saved = JSON.parse(raw); if (saved && saved.answers) state = Object.assign(state, saved); }
    } catch (e) { /* storage unavailable — run in-memory */ }

    var persist = debounce(function () {
      try { localStorage.setItem(key, JSON.stringify(state)); } catch (e) { /* ignore quota/private mode */ }
    }, 120);

    return {
      state: state,
      get: function (id) { return state.answers[id]; },
      set: function (id, val) { state.answers[id] = val; persist(); },
      setMeta: function (patch) { Object.assign(state, patch); persist(); },
      reset: function () {
        state.answers = {}; state.index = 0; state.started = false; state.completed = false;
        try { localStorage.removeItem(key); } catch (e) {}
      }
    };
  }

  /* ======================================================================
   * [6] RECOMMENDATION ENGINE
   * ==================================================================== */

  // Weights are centralized so the logic is easy to tune / extend.
  var W = {
    concern: 10, priorityConcern: 16, ingredientConcern: 6, priorityIngredient: 4,
    skinExact: 8, skinAll: 4, skinMismatch: -9,
    sensitiveMatch: 8, sensitiveFeature: 3, sensitiveFragrance: 4, sensitiveStrongPenalty: 11,
    procedureCare: 14,
    pref: 5, sunscreenType: 10, sunscreenTypeMiss: -6, shade: 8,
    feature: 2, spfLevel: 4, retinolWant: 10, retinolPrev: 4, retinolNeverPenalty: 7,
    available: 0, brandDiversity: 3
  };

  function Engine(rawProducts, cfg) {
    this.cfg = cfg;
    // SKU -> branch pre-seed map (global, identical across clinics).
    this.skuBranch = (window.SkinQuizCatalog && window.SkinQuizCatalog.skuBranch) || {};
    this.setProducts(rawProducts);
  }

  // (Re)load the catalogue — used at boot and by the live background refresh.
  Engine.prototype.setProducts = function (rawProducts) {
    this.products = this.prepare(rawProducts);
    // Catalogue-derived facts consumed by the flow builder.
    this.catalogue = { prefValueSet: this.collectPrefValues(), branches: this.collectBranches() };
  };

  Engine.prototype.prepare = function (raw) {
    var self = this;
    return (raw || []).map(function (p) {
      var cpp = p.cpp || {};
      var ptNorm = toArray(cpp.producttype).map(norm);
      var formNorm = toArray(cpp.productform).map(norm);
      var aaNorm = toArray(cpp.applicationarea).map(norm);
      var stepKeys = stepsForNorm(ptNorm);
      // Multi-product kits/sets are typed with several step producttypes at once
      // and would masquerade as a single routine step — exclude them.
      var isBundle = formNorm.indexOf('kit') !== -1 || ptNorm.indexOf('skincaresets') !== -1;
      // Pre/post-procedure aftercare (e.g. post-injection serums) is meant for
      // treatment recovery, not an everyday routine — heavily de-prioritized.
      var isProcedureCare = ptNorm.some(function (t) { return t.indexOf('procedurecare') !== -1; });
      // Branch: SKU pre-seed first, then live classification.
      var branch = (p.sku && self.skuBranch[p.sku]) || classifyBranchLive(p.category, ptNorm, aaNorm, p.title);
      return {
        branch: branch, category: p.category || '', sku: p.sku || '',
        branchStepKeys: BRANCH_STEPS[branch] ? branchStepsFor(branch, ptNorm) : [],
        raw: p,
        id: p.id, title: p.title, handle: p.handle, url: p.url, vendor: p.vendor || '',
        image: p.image, imageAlt: p.image_alt || p.title,
        price: p.price, compareAt: p.compare_at_price, priceAmount: p.price_amount || 0,
        available: p.available !== false, variantId: p.variant_id,
        stepKeys: stepKeys, isBundle: isBundle, isProcedureCare: isProcedureCare,
        // normalized metafield views
        producttype: toArray(cpp.producttype),
        producttypeNorm: ptNorm,
        skintype: toArray(cpp.skintype),
        skintypeSet: normSet(cpp.skintype),
        concern: toArray(cpp.concern),
        concernSet: normSet(cpp.concern),
        keyingredient: toArray(cpp.keyingredient),
        preferences: toArray(cpp.preferences),
        preferencesSet: normSet(cpp.preferences),
        applicationareaNorm: aaNorm,
        features: toArray(cpp.features),
        featuresSet: normSet(cpp.features),
        spf: toArray(cpp.spf),
        sunscreentypeSet: normSet(cpp.sunscreentype),
        shadeSet: normSet(cpp.shade),
        benefits: cpp.benefits || '',
        ingredients: cpp.ingredients || '',
        howtouse: cpp.howtouse || '',
        ing: analyzeIngredients(cpp.keyingredient),
        tags: toArray(p.tags)
      };
    });
  };

  Engine.prototype.collectPrefValues = function () {
    var set = Object.create(null);
    this.products.forEach(function (p) { Object.keys(p.preferencesSet).forEach(function (k) { set[k] = true; }); });
    return set;
  };

  // Which branches actually have (available) products in this store — used to
  // hide empty branches from the picker.
  Engine.prototype.collectBranches = function () {
    var c = {};
    this.products.forEach(function (p) { if (p.available) c[p.branch] = (c[p.branch] || 0) + 1; });
    return c;
  };

  // Which non-skin branch steps a product's types can serve.
  function branchStepsFor(branch, ptNorm) {
    var steps = BRANCH_STEPS[branch] || [];
    var out = [];
    steps.forEach(function (step) {
      if (ptNorm.some(function (t) { return step.types.indexOf(t) !== -1; })) out.push(step.key);
    });
    return out;
  }

  // Which routine step(s) can a product serve? (precomputed in prepare)
  Engine.prototype.stepsFor = function (p) { return p.stepKeys; };

  // Is the product usable in a facial routine (not a body/scalp/hair/supplement)?
  Engine.prototype.isFacial = function (p) {
    var areas = p.applicationareaNorm;
    if (areas.length === 0) return true; // unknown — allow, scoring handles the rest
    var onFace = areas.some(function (a) { return FACE_AREAS.indexOf(a) !== -1; });
    var offFace = areas.some(function (a) { return OFFACE_AREAS.indexOf(a) !== -1; });
    return onFace || !offFace;
  };

  /** Score a single product for a given step against the user profile.
   *  Returns { score, signals } where signals feed the rationale generator.   */
  Engine.prototype.score = function (p, stepKey, profile) {
    var s = 0;
    var signals = { concerns: [], ingredients: [], prefs: [], skin: false, features: [] };

    // ---- Concern coverage (the dominant signal) --------------------------
    profile.concerns.forEach(function (cid) {
      var concern = CONCERN_BY_ID[cid];
      if (!concern) return;
      // Eye-area concerns only steer the eye step.
      if (concern.eyeOnly && stepKey !== 'eye') return;
      var isPriority = (cid === profile.priority);
      var addressed = false;

      // (a) explicit cpp.concern metafield overlap
      if (intersects(p.concernSet, concern.tokens)) {
        s += isPriority ? W.priorityConcern : W.concern;
        addressed = true;
      }
      // (b) ingredient-level evidence
      var ingRules = p.ing.byConcern[cid];
      if (ingRules && ingRules.length) {
        s += W.ingredientConcern + (isPriority ? W.priorityIngredient : 0);
        addressed = true;
        signals.ingredients.push(ingRules[0].blurb);
      }
      if (addressed) signals.concerns.push({ label: concern.label, priority: isPriority });
    });

    // ---- Skin-type fit ---------------------------------------------------
    var skinType = SKIN_BY_ID[profile.skintype];
    if (skinType) {
      var hasAny = Object.keys(p.skintypeSet).length > 0;
      var compatible = intersects(p.skintypeSet, skinType.compat);
      var allTypes = p.skintypeSet[norm('All Skin Types')] != null;
      if (compatible) { s += allTypes && !skinCompatSpecific(p, skinType) ? W.skinAll : W.skinExact; signals.skin = true; }
      else if (hasAny) { s += W.skinMismatch; } // product targets other skin types
    }

    // ---- Sensitivity -----------------------------------------------------
    if (profile.sensitivity === 'high' || profile.sensitivity === 'some') {
      var strong = profile.sensitivity === 'high';
      if (p.skintypeSet[norm('Sensitive')] || p.skintypeSet[norm('Rosacea Prone')]) s += W.sensitiveMatch;
      if (p.featuresSet[norm('Calming')] || p.featuresSet[norm('Soothing')] || p.featuresSet[norm('Dermatologist Tested')]) s += W.sensitiveFeature;
      if (p.preferencesSet[norm('Fragrance Free')]) s += W.sensitiveFragrance;
      // Gate potent actives for reactive skin (unless they specifically want max results & are advanced)
      if (p.ing.hasStrong && !(profile.maxResults && profile.experience === 'advanced')) {
        s -= strong ? W.sensitiveStrongPenalty : Math.round(W.sensitiveStrongPenalty / 2);
      }
    }

    // ---- Preferences -----------------------------------------------------
    profile.prefs.forEach(function (pref) {
      if (pref.kind === 'pref' && p.preferencesSet[norm(pref.match)]) { s += W.pref; signals.prefs.push(pref.label); }
      else if (pref.kind === 'sunscreentype' && stepKey === 'spf') {
        if (p.sunscreentypeSet[norm(pref.match)]) { s += W.sunscreenType; signals.prefs.push(pref.label); }
        else s += W.sunscreenTypeMiss;
      }
      else if (pref.kind === 'shade' && stepKey === 'spf') {
        if (p.shadeSet[norm(pref.match)]) { s += W.shade; signals.prefs.push(pref.label); }
      }
    });

    // ---- Feature ↔ concern alignment (small nudges) ----------------------
    var featureConcern = [
      ['Brightening', ['pigmentation', 'sun_damage', 'texture']],
      ['Firming', ['firmness', 'fine_lines', 'wrinkles']],
      ['Calming', ['redness']], ['Soothing', ['redness']],
      ['Hydrating', ['dryness']], ['Moisturizing', ['dryness']],
      ['Smoothing', ['texture', 'fine_lines']], ['Plumping', ['firmness', 'dryness']]
    ];
    featureConcern.forEach(function (fc) {
      if (p.featuresSet[norm(fc[0])] && fc[1].some(function (c) { return profile.concerns.indexOf(c) !== -1; })) {
        s += W.feature;
      }
    });

    // ---- Step-specific -----------------------------------------------------
    if (stepKey === 'spf') {
      var spfNum = maxSpf(p.spf);
      if (spfNum >= 30) s += W.spfLevel;
      if (profile.outdoor === 'high' && spfNum >= 50) s += W.spfLevel;
      if (p.featuresSet[norm('Water Resistant')] && profile.outdoor === 'high') s += 2;
    }
    if (stepKey === 'treatment') {
      var isRetinol = p.ing.rules.some(function (r) { return r.id === 'retinoid'; }) || /retin(ol|al|oid)/i.test(p.title);
      if (isRetinol) {
        if (profile.retinol === 'currently') s += W.retinolWant;
        else if (profile.retinol === 'previously') s += W.retinolPrev;
        else if (profile.retinol === 'never') s -= (profile.experience === 'new' ? W.retinolNeverPenalty : Math.round(W.retinolNeverPenalty / 2));
      }
    }

    // ---- Age bias --------------------------------------------------------
    if (profile.ageWeightAnti && p.ing.byConcern['firmness'] || (profile.ageWeightAnti && p.ing.byConcern['wrinkles'])) s += 2;
    if (profile.ageWeightPrevent && (p.ing.rules.some(function (r) { return r.id === 'antiox'; }))) s += 2;

    // ---- Cross-specialization: prefer dedicated products for core steps ----
    if (CORE_STEPS[stepKey] && (p.stepKeys.indexOf('eye') !== -1 || p.stepKeys.indexOf('lip') !== -1)) s -= 4;

    // ---- Procedure aftercare doesn't belong in an everyday routine --------
    if (p.isProcedureCare) s -= W.procedureCare;

    // ---- Availability ----------------------------------------------------
    if (!p.available) s -= 1000; // never surface an unbuyable product

    return { score: s, signals: signals };
  };

  // Helper: does the product name a skin type specific to this user (not just "all")?
  function skinCompatSpecific(p, skinType) {
    return skinType.compat.some(function (c) {
      return norm(c) !== norm('All Skin Types') && p.skintypeSet[norm(c)] != null;
    });
  }

  function maxSpf(spfArr) {
    var max = 0;
    toArray(spfArr).forEach(function (v) {
      String(v).split(/[^0-9]+/).forEach(function (n) { var x = parseInt(n, 10); if (x && x > max) max = x; });
    });
    return max;
  }

  // Time-of-day inference for a treatment: 'pm' | 'am' | 'any'
  function timeOfDay(p) {
    var isRetinoidOrAcid = p.ing.rules.some(function (r) { return r.id === 'retinoid' || r.id === 'aha' || r.id === 'bha'; });
    var howto = norm(p.howtouse);
    if (isRetinoidOrAcid || /night|pm|evening/.test(p.howtouse.toLowerCase())) return 'pm';
    var isAntioxOrC = p.ing.rules.some(function (r) { return r.id === 'vitc' || r.id === 'antiox'; });
    if (isAntioxOrC || /morning|\bam\b|daytime|under (spf|sunscreen)/.test(p.howtouse.toLowerCase())) return 'am';
    return 'any';
  }

  // Dominant ingredient "family" — used to avoid stacking duplicate actives.
  function dominantFamily(p) {
    return p.ing.rules.length ? p.ing.rules[0].id : ('form:' + norm(p.producttype[0] || p.title));
  }

  function optVal(x) { return (x && typeof x === 'object') ? x.value : x; }
  function optIds(arr) { return (arr || []).map(optVal).filter(Boolean); }

  /** Build the full routine from answers. Dispatches by branch. */
  Engine.prototype.recommend = function (answers) {
    var branch = optVal(answers.branch);
    if (!branch) {
      var present = this.catalogue.branches || {};
      var only = Object.keys(BRANCHES).filter(function (b) { return (present[b] || 0) > 0; });
      branch = only.length === 1 ? only[0] : 'skin';
    }
    if (branch !== 'skin') return this.recommendBranch(branch, answers);
    return this.recommendSkin(answers);
  };

  /** Generic assembler for Body / Hair / Wellness branches. */
  Engine.prototype.recommendBranch = function (branch, answers) {
    var self = this;
    var steps = BRANCH_STEPS[branch] || [];
    var selKey = branch === 'body' ? 'body_concerns' : branch === 'hair' ? 'hair_goal' : 'wellness_goals';
    var selIds = optIds(answers[selKey]);
    var selConcerns = selIds.map(function (id) { return BRANCH_CONCERN_BY_ID[id]; }).filter(Boolean);
    var prefs = (answers.preferences || []).concat(answers.wellness_diet || []);
    var wantSupp = branch === 'hair' ? (optVal(answers.hair_supplements) !== 'no') : true;

    var pool = this.products.filter(function (p) {
      return p.branch === branch && p.available && !p.isBundle;
    });

    function scoreProduct(p) {
      var s = 0, sig = { concerns: [], prefs: [], ingredients: [] };
      var blob = norm([p.title, p.category, p.producttype.join(' '), p.benefits, p.keyingredient.join(' ')].join(' '));
      selConcerns.forEach(function (c, i) {
        var weight = i === 0 ? 12 : 8;
        var hit = false;
        if (c.tokens && c.tokens.length && intersects(p.concernSet, c.tokens)) { s += weight; hit = true; }
        if (c.kw && c.kw.some(function (k) { return blob.indexOf(norm(k)) !== -1; })) { s += Math.round(weight * 0.7); hit = true; }
        if (hit) sig.concerns.push(c.label);
      });
      // Ingredient evidence (reuses the skin knowledge base where relevant).
      p.ing.rules.forEach(function (r) { if (sig.ingredients.length < 2) sig.ingredients.push(r.blurb); });
      prefs.forEach(function (pref) {
        if (pref.kind === 'pref' && p.preferencesSet[norm(pref.match)]) { s += 4; sig.prefs.push(pref.label); }
      });
      return { score: s, signals: sig };
    }

    var usedIds = {}, usedFamilies = {}, groups = [];
    steps.forEach(function (step) {
      if (step.key === 'support' && !wantSupp) return;
      var cands = pool
        .filter(function (p) { return step.matchAll || p.branchStepKeys.indexOf(step.key) !== -1; })
        .map(function (p) { var r = scoreProduct(p); return { product: p, score: r.score, signals: r.signals, step: step.label }; })
        .sort(function (a, b) { return b.score - a.score; });
      // Prefer positively-matched products; for essential steps fall back to top.
      var positive = cands.filter(function (c) { return c.score > 0; });
      var usable = positive.length ? positive : (step.optional ? [] : cands);
      var picked = [];
      for (var i = 0; i < usable.length && picked.length < step.slots; i++) {
        var cand = usable[i];
        if (usedIds[cand.product.id]) continue;
        if (picked.length && usedFamilies[dominantFamily(cand.product)]) continue;
        usedIds[cand.product.id] = true;
        usedFamilies[dominantFamily(cand.product)] = true;
        picked.push(cand);
      }
      if (picked.length) groups.push({ key: step.key, label: step.label, cards: picked });
    });

    var all = [];
    groups.forEach(function (g) {
      g.cards.forEach(function (card) { card.why = self.branchRationale(card, branch); all.push(card); });
    });

    return {
      branch: branch, groups: groups, all: dedupeCards(all),
      profile: { branch: branch, selected: selIds, selectedLabels: selConcerns.map(function (c) { return c.label; }) }
    };
  };

  Engine.prototype.branchRationale = function (card, branch) {
    var sig = card.signals, parts = [];
    var concerns = uniq(sig.concerns).slice(0, 2).map(function (c) { return c.toLowerCase(); });
    if (concerns.length) parts.push('Matched to your focus on ' + listToSentence(concerns) + '.');
    var ings = uniq(sig.ingredients).slice(0, 2);
    if (ings.length) parts.push('Formulated with ' + listToSentence(ings) + '.');
    var prefs = uniq(sig.prefs).slice(0, 2);
    if (prefs.length) parts.push(listToSentence(prefs) + ', matching your preferences.');
    if (!parts.length) {
      var b = parseBenefits(card.product.benefits)[0];
      parts.push(b || ('A clinician favourite for ' + (BRANCHES[branch] || {}).label.toLowerCase() + '.'));
    }
    return parts.slice(0, 2).join(' ');
  };

  /** Build the facial-skincare routine (the original, richer engine). */
  Engine.prototype.recommendSkin = function (answers) {
    var self = this;
    var prefs = answers.preferences || [];
    // Answers for multi-selects are stored as option objects ({value,label});
    // the engine works in concern *id* strings. Normalize defensively so it
    // also tolerates plain-string answers set programmatically.
    var val = function (x) { return (x && typeof x === 'object') ? x.value : x; };
    var concerns = (answers.concerns || []).map(val).filter(Boolean);
    var priority = val(answers.priority) || concerns[0];
    var profile = {
      skintype: answers.skintype,
      sensitivity: answers.sensitivity,
      experience: answers.experience,
      concerns: concerns,
      priority: priority,
      retinol: answers.retinol,
      outdoor: answers.outdoor,
      spfDaily: answers.spf_daily,
      prefs: prefs,
      minimal: prefs.some(function (p) { return p.value === 'minimal'; }),
      maxResults: prefs.some(function (p) { return p.value === 'maximum'; }),
      ageWeightAnti: answers.age === '45_54' || answers.age === '55plus',
      ageWeightPrevent: answers.age === 'under25' || answers.age === '25_34'
    };

    // Rank candidates per step.
    var ranked = {};
    Object.keys(STEPS).forEach(function (key) { ranked[key] = []; });
    this.products.forEach(function (p) {
      if (p.isBundle || !self.isFacial(p)) return;
      self.stepsFor(p).forEach(function (stepKey) {
        var r = self.score(p, stepKey, profile);
        if (r.score > -500) ranked[stepKey].push({ product: p, score: r.score, signals: r.signals, tod: timeOfDay(p) });
      });
    });
    Object.keys(ranked).forEach(function (k) { ranked[k].sort(function (a, b) { return b.score - a.score; }); });

    var usedIds = {};      // no product twice across the routine
    var usedFamilies = {}; // avoid duplicate dominant actives

    function take(stepKey, opts) {
      opts = opts || {};
      var list = ranked[stepKey] || [];
      for (var i = 0; i < list.length; i++) {
        var cand = list[i];
        if (usedIds[cand.product.id]) continue;
        if (opts.minScore != null && cand.score < opts.minScore) continue;
        if (opts.tod && cand.tod !== opts.tod && cand.tod !== 'any') continue;
        if (opts.avoidFamily && usedFamilies[dominantFamily(cand.product)]) continue;
        usedIds[cand.product.id] = true;
        usedFamilies[dominantFamily(cand.product)] = true;
        return cand;
      }
      return null;
    }

    // ---- Core steps ------------------------------------------------------
    var cleanser = take('cleanser');
    var moisturizer = take('moisturizer');
    var spf = take('spf');

    // ---- Treatments (1–2, complementary + time-balanced) -----------------
    var treatmentSlots = profile.minimal || profile.experience === 'new' ? 1 : 2;
    var treatments = [];
    var primary = take('treatment');
    if (primary) treatments.push(primary);
    if (treatmentSlots === 2 && primary) {
      // Prefer a second treatment for the opposite time-of-day and a different family.
      var wantTod = primary.tod === 'pm' ? 'am' : (primary.tod === 'am' ? 'pm' : null);
      var second = wantTod ? take('treatment', { tod: wantTod, avoidFamily: true, minScore: 1 }) : null;
      if (!second) second = take('treatment', { avoidFamily: true, minScore: 1 });
      if (second) treatments.push(second);
    }

    // ---- Eye: core if a relevant concern, else an enhancement ------------
    var eyeRelevant = profile.concerns.some(function (c) { return ['dark_circles', 'fine_lines', 'wrinkles', 'firmness'].indexOf(c) !== -1; });
    var eye = take('eye', { minScore: eyeRelevant ? -100 : 1 });
    var eyeIsCore = !!(eye && (eyeRelevant || profile.experience === 'advanced'));

    // ---- Optional enhancements ------------------------------------------
    var enhancements = [];
    if (eye && !eyeIsCore) enhancements.push(tagStep(eye, 'Eye'));
    // Exfoliant upgrade (skip for brand-new / very sensitive users)
    if (!(profile.experience === 'new' || profile.sensitivity === 'high' || profile.minimal)) {
      var exfo = take('exfoliant', { avoidFamily: true, minScore: 1 });
      if (exfo) enhancements.push(tagStep(exfo, 'Weekly Exfoliant'));
    }
    // A lip treatment as a finishing touch
    var lip = take('lip', { minScore: -100 });
    if (lip) enhancements.push(tagStep(lip, 'Lip'));
    // A third/booster treatment for advanced + maximum-results users
    if (profile.maxResults && profile.experience === 'advanced') {
      var booster = take('treatment', { avoidFamily: true, minScore: 1 });
      if (booster) enhancements.push(tagStep(booster, 'Booster'));
    }

    // ---- Assign time-of-day (each product once) --------------------------
    // Rather than repeating shared products in a Morning + Evening list, each
    // product appears once tagged with WHEN to use it: 'am' | 'pm' | 'both'.
    var amTreatment = null, pmTreatment = null;
    treatments.forEach(function (t) {
      if (t.tod === 'pm' && !pmTreatment) pmTreatment = t;
      else if (t.tod === 'am' && !amTreatment) amTreatment = t;
      else if (!amTreatment) amTreatment = t;
      else if (!pmTreatment) pmTreatment = t;
    });

    var core = compact([
      cleanser && tagStep(cleanser, 'Cleanser', 'both'),
      amTreatment && tagStep(amTreatment, 'Treatment', 'am'),
      pmTreatment && tagStep(pmTreatment, 'Treatment', 'pm'),
      moisturizer && tagStep(moisturizer, 'Moisturizer', 'both'),
      spf && tagStep(spf, 'SPF', 'am'),
      eyeIsCore && eye && tagStep(eye, 'Eye', 'both')
    ]);

    // Attach rationale to every selected card.
    [core, enhancements].forEach(function (grp) {
      grp.forEach(function (card) { card.why = self.rationale(card, profile); });
    });

    return {
      branch: 'skin',
      profile: profile,
      core: core,
      enhancements: enhancements,
      // de-duplicated flat list for "add entire routine"
      all: dedupeCards(core.concat(enhancements))
    };
  };

  function tagStep(cand, stepLabel, when) {
    return { product: cand.product, score: cand.score, signals: cand.signals, tod: cand.tod, step: stepLabel, when: when || null };
  }
  function compact(arr) { return arr.filter(Boolean); }
  function dedupeCards(cards) {
    var seen = {}, out = [];
    cards.forEach(function (c) { if (!seen[c.product.id]) { seen[c.product.id] = true; out.push(c); } });
    return out;
  }

  /** Generate the "Why we chose it" rationale from matched signals + metafields.
   *  Draws exclusively on real clinician data, never generic marketing copy.    */
  Engine.prototype.rationale = function (card, profile) {
    var sig = card.signals;
    var parts = [];

    // 1) Skin-type + concern framing
    var skinLabel = (SKIN_BY_ID[profile.skintype] || {}).label;
    var concernLabels = uniq(sig.concerns.map(function (c) { return c.label.toLowerCase(); })).slice(0, 2);
    if (skinLabel && concernLabels.length) {
      parts.push('Matched to your ' + skinLabel.toLowerCase() + ' skin and focus on ' + listToSentence(concernLabels) + '.');
    } else if (concernLabels.length) {
      parts.push('Targets your focus on ' + listToSentence(concernLabels) + '.');
    } else if (skinLabel && sig.skin) {
      parts.push('Formulated for ' + skinLabel.toLowerCase() + ' skin.');
    }

    // 2) Ingredient evidence (the clinician detail)
    var ing = uniq(sig.ingredients).slice(0, 2);
    if (ing.length) {
      parts.push('Formulated with ' + listToSentence(ing) + '.');
    }

    // 3) Preference honouring
    var prefs = uniq(sig.prefs).slice(0, 2);
    if (prefs.length) {
      parts.push(listToSentence(prefs) + ', matching your preferences.');
    }

    // 4) Fallback to a benefit line if we somehow have nothing specific
    if (parts.length === 0) {
      var firstBenefit = parseBenefits(card.product.benefits)[0];
      if (firstBenefit) parts.push(firstBenefit);
      else parts.push('A clinician favourite that fits your profile.');
    }

    // Keep it to two crisp sentences.
    return parts.slice(0, 3).join(' ');
  };

  function uniq(arr) { var s = {}, o = []; arr.forEach(function (x) { var k = String(x); if (!s[k]) { s[k] = 1; o.push(x); } }); return o; }

  function parseBenefits(text) {
    return String(text || '')
      .split(/\r?\n|(?:•)|(?:^-\s)/m)
      .map(function (l) { return l.replace(/^[•\-•\s]+/, '').trim(); })
      .filter(function (l) { return l.length > 2; })
      .slice(0, 6);
  }

  /* ======================================================================
   * [7] UI RENDERER
   * ==================================================================== */
  function Quiz(root, data) {
    this.root = root;
    this.cfg = data.config;
    this.engine = new Engine(data.products, data.config);
    this.store = createStore('cpp-skinquiz:' + (this.cfg.sectionId || 'default'));
    // Base flow now; the branch's questions are appended once a branch is
    // chosen (or restored on resume).
    this.flow = buildFlow(this.engine.catalogue, this.cfg, optVal(this.store.get('branch')));

    // Horizon 4 uses one global color palette. Keep the scope class for surface
    // styling without attaching legacy color-scheme classes.
    this.scopeCls = ' sq-scope';
    this.consultScopeCls = ' sq-scope';

    this.refs = {
      progress: root.querySelector('[data-sq-progress]'),
      back: root.querySelector('[data-sq-back]'),
      bar: root.querySelector('[data-sq-progressbar]'),
      fill: root.querySelector('[data-sq-progressfill]'),
      count: root.querySelector('[data-sq-stepcount]'),
      panels: root.querySelector('[data-sq-panels]'),
      welcome: root.querySelector('[data-sq-welcome]'),
      mount: root.querySelector('[data-sq-mount]'),
      toast: root.querySelector('[data-sq-toast]'),
      toastText: root.querySelector('[data-sq-toast-text]'),
      customerForm: root.querySelector('[data-sq-customer-form] form')
    };

    this.index = -1; // -1 = welcome
    this.bind();
    this.refreshProducts(); // pull the freshest catalogue in the background
    this.maybeResume();
  }

  /* ---- Background live-refresh -------------------------------------------
     The server-rendered payload is accurate at render but a CDN-cached page
     can be stale. While the shopper fills the lead card / early questions, we
     re-pull this section via Shopify's Section Rendering API and hot-swap the
     catalogue so recommendations run on the latest admin data. Fail-open.    */
  Quiz.prototype.refreshProducts = function () {
    var self = this;
    if (this.cfg.liveRefresh === false || !this.cfg.sectionId) return Promise.resolve(false);
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(this.cfg.sectionId);
    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var el = doc.querySelector('[data-sq-products]');
        if (!el) return;
        var fresh = JSON.parse(el.textContent);
        if (!Array.isArray(fresh) || !fresh.length) return;
        self.engine.setProducts(fresh);
        // Refresh dynamic option sets (preferences) if the shopper hasn't
        // reached them yet — avoids mutating a question already on screen.
        var prefsIdx = -1;
        self.flow.forEach(function (q, i) { if (q.id === 'preferences') prefsIdx = i; });
        if (self.index < prefsIdx) {
          var q = self.flow[prefsIdx];
          if (q) q.options = buildPreferenceOptions(self.engine.catalogue);
        }
        self.dispatch('productsrefreshed', { count: fresh.length });
        return true;
      })
      .catch(function () { return false; /* keep the server-rendered snapshot */ });
  };

  Quiz.prototype.bind = function () {
    var self = this;
    var startBtn = this.root.querySelector('[data-sq-start]');
    if (startBtn) startBtn.addEventListener('click', function () { self.start(); });
    this.refs.back.addEventListener('click', function () { self.back(); });

    // Keyboard: Enter advances single-select when an option is focused elsewhere handled per-panel.
    document.addEventListener('keydown', function (e) {
      if (!self.active) return;
      if (e.key === 'Enter' && self.continueBtn && !self.continueBtn.disabled &&
          document.activeElement && document.activeElement.tagName !== 'BUTTON') {
        self.continueBtn.click();
      }
    });
  };

  // Rebuild the flow to include the chosen branch's questions, preserving the
  // base steps (contact + branch picker) and their answers.
  Quiz.prototype.rebuildFlow = function () {
    this.flow = buildFlow(this.engine.catalogue, this.cfg, optVal(this.store.get('branch')));
  };

  Quiz.prototype.maybeResume = function () {
    // If the user previously completed, keep it simple and restart fresh on load.
    // (Auto-save primarily protects against accidental refresh mid-quiz.)
    var st = this.store.state;
    if (st.started && !st.completed && typeof st.index === 'number' && st.index >= 0) {
      this.rebuildFlow(); // restore branch-specific questions
      this.refs.welcome.hidden = true;
      this.refs.progress.hidden = false;
      this.goTo(Math.min(st.index, this.flow.length - 1), 'none');
    }
  };

  Quiz.prototype.start = function () {
    // A completed consultation restarts fresh — stale answers from a previous
    // run must not leak into a new one.
    if (this.store.state.completed) this.store.reset();
    this.store.setMeta({ started: true });
    this.dispatch('start', {});
    this.refs.progress.hidden = false;
    this.animateOut(this.refs.welcome, () => { this.refs.welcome.hidden = true; });
    this.goTo(0, 'forward');
  };

  Quiz.prototype.updateProgress = function () {
    var total = this.flow.length;
    var current = Math.min(this.index + 1, total);
    var pct = Math.round((current / total) * 100);
    this.refs.fill.style.width = pct + '%';
    this.refs.bar.setAttribute('aria-valuenow', String(pct));
    this.refs.count.textContent = current + ' / ' + total;
    this.refs.back.disabled = this.index <= 0;
  };

  // Navigate to a question index. dir: 'forward' | 'back' | 'none'
  Quiz.prototype.goTo = function (index, dir) {
    var self = this;
    // Handle the dynamic priority step (skip if <=1 concern).
    var q = this.flow[index];
    if (q && q.id === 'priority') {
      var concerns = this.store.get('concerns') || [];
      if (concerns.length <= 1) {
        var only = concerns[0];
        this.store.set('priority', only && only.value ? only.value : only);
        return this.goTo(dir === 'back' ? index - 1 : index + 1, dir);
      }
    }
    if (index < 0) { // back past first question -> welcome
      this.index = -1;
      this.refs.progress.hidden = true;
      this.refs.welcome.hidden = false;
      this.animateIn(this.refs.welcome);
      return;
    }
    if (index >= this.flow.length) { return this.finish(); }

    this.index = index;
    this.store.setMeta({ index: index });
    this.updateProgress();

    var panel = this.renderQuestion(this.flow[index]);
    this.swapPanel(panel, dir);
  };

  Quiz.prototype.next = function () {
    this.goTo(this.index + 1, 'forward');
  };
  Quiz.prototype.back = function () {
    if (this.index <= 0) return this.goTo(-1, 'back');
    this.goTo(this.index - 1, 'back');
  };

  // Replace the active panel with a new one, animating the transition.
  Quiz.prototype.swapPanel = function (panel, dir) {
    var self = this;
    var mount = this.refs.mount;
    var old = this.active;
    this.active = panel;
    mount.appendChild(panel);
    // enter animation
    panel.classList.add('is-entering');
    if (old) {
      this.animateOut(old, function () { if (old.parentNode) old.parentNode.removeChild(old); });
    }
    // move focus to the heading for screen-reader continuity
    var h = panel.querySelector('h2, h1');
    if (h) { h.setAttribute('tabindex', '-1'); setTimeout(function () { h.focus({ preventScroll: false }); }, 60); }
    this.scrollTop();
  };

  Quiz.prototype.animateIn = function (node) {
    node.classList.remove('is-entering'); void node.offsetWidth; node.classList.add('is-entering');
  };
  Quiz.prototype.animateOut = function (node, done) {
    if (prefersReducedMotion()) { done && done(); return; }
    node.style.transition = 'opacity .25s ease'; node.style.opacity = '0';
    setTimeout(function () { node.style.opacity = ''; node.style.transition = ''; done && done(); }, 220);
  };
  Quiz.prototype.scrollTop = function () {
    var top = this.root.getBoundingClientRect().top + window.pageYOffset - 12;
    if (window.pageYOffset > top || this.index > 0) {
      window.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
  };

  // -------- Question rendering ------------------------------------------
  Quiz.prototype.renderQuestion = function (q) {
    var self = this;
    if (q.type === 'rank') return this.renderRank(q);
    if (q.type === 'contact') return this.renderContact(q);

    var panel = elFromHTML('<section class="sq-panel"></section>');
    var head =
      '<div class="sq-q__head sq-anim">' +
        '<p class="sq-eyebrow">' + esc(q.group) + '</p>' +
        '<h2 class="sq-h2">' + esc(q.title) + '</h2>' +
        (q.subtitle ? '<p class="sq-lede sq-q__sub">' + esc(q.subtitle) + '</p>' : '') +
      '</div>';

    var isMulti = q.type === 'multi';
    var useChips = isMulti && q.options.length > 6;
    var gridClass = useChips ? 'sq-options--chips' : (q.options.length >= 5 ? 'sq-options--tri' : (q.options.length <= 2 ? 'sq-options--duo' : ''));
    var wrap = '<div class="sq-anim"><div class="sq-options ' + gridClass + '" role="' + (isMulti ? 'group' : 'radiogroup') + '" aria-label="' + esc(q.title) + '">';

    var current = this.store.get(q.id);
    var selected = isMulti ? (Array.isArray(current) ? current.slice() : []) : current;
    var selValues = isMulti ? selected.map(function (o) { return o.value; }) : [];

    q.options.forEach(function (opt) {
      var isSel = isMulti ? selValues.indexOf(opt.value) !== -1 : selected === opt.value;
      wrap += self.optionMarkup(opt, isMulti, isSel, useChips);
    });
    wrap += '</div>';
    if (isMulti && q.max) {
      wrap += '<div class="sq-selmeta sq-anim"><span class="sq-selmeta__count" data-sq-selcount></span>' +
              '<span class="sq-hint">Select up to ' + q.max + '</span></div>';
    }
    wrap += '</div>';

    panel.innerHTML = head + wrap + this.footerMarkup(q, isMulti);
    this.wireQuestion(panel, q, isMulti, useChips);
    return panel;
  };

  Quiz.prototype.optionMarkup = function (opt, isMulti, isSel, chip) {
    if (chip) {
      return '<button type="button" class="sq-chip' + this.scopeCls + (isSel ? ' is-selected' : '') + '" role="checkbox" aria-checked="' + isSel + '" data-value="' + esc(opt.value) + '">' +
        esc(opt.label) +
        '<span class="sq-chip__x">' + ICONS.check + '</span>' +
      '</button>';
    }
    var mark = '<span class="sq-opt__mark">' + ICONS.check + '</span>';
    return '<button type="button" class="sq-opt' + this.scopeCls + (isMulti ? ' sq-opt--multi' : '') + (isSel ? ' is-selected' : '') +
        '" role="' + (isMulti ? 'checkbox' : 'radio') + '" aria-checked="' + isSel + '" data-value="' + esc(opt.value) + '">' +
        '<span class="sq-opt__body">' +
          '<span class="sq-opt__title">' + esc(opt.label) + '</span>' +
          (opt.desc ? '<span class="sq-opt__desc">' + esc(opt.desc) + '</span>' : '') +
        '</span>' + mark +
      '</button>';
  };

  Quiz.prototype.footerMarkup = function (q, isMulti) {
    // Single-select auto-advances, so it only needs Back (in header). Multi/rank need Continue.
    if (!isMulti && q.type !== 'rank') return '';
    var label = q.optional ? 'Continue' : 'Continue';
    return '<div class="sq-footer sq-anim">' +
        (q.optional ? '<button type="button" class="sq-btn sq-btn--ghost" data-sq-skip>Skip</button>' : '') +
        '<button type="button" class="sq-btn sq-btn--primary" data-sq-continue>' + label + ' ' + ICONS.arrow + '</button>' +
      '</div>';
  };

  Quiz.prototype.wireQuestion = function (panel, q, isMulti, chip) {
    var self = this;
    var opts = Array.prototype.slice.call(panel.querySelectorAll('[data-value]'));
    this.continueBtn = panel.querySelector('[data-sq-continue]');
    var skipBtn = panel.querySelector('[data-sq-skip]');
    var selCount = panel.querySelector('[data-sq-selcount]');

    function currentSelection() {
      return opts.filter(function (o) { return o.classList.contains('is-selected'); });
    }
    function refreshMulti() {
      var sel = currentSelection();
      if (q.max) {
        var atMax = sel.length >= q.max;
        opts.forEach(function (o) {
          var s = o.classList.contains('is-selected');
          o.classList.toggle('is-disabled', atMax && !s && chip);
          if (!chip) o.style.opacity = (atMax && !s) ? '0.5' : '';
        });
        if (selCount) selCount.textContent = sel.length + ' selected';
      }
      if (self.continueBtn) self.continueBtn.disabled = sel.length < (q.min || 0);
      // persist
      var payload = sel.map(function (o) {
        var opt = findOpt(q, o.getAttribute('data-value'));
        return opt;
      });
      self.store.set(q.id, payload);
    }

    opts.forEach(function (o) {
      o.addEventListener('click', function () {
        if (isMulti) {
          var isSel = o.classList.contains('is-selected');
          if (!isSel && q.max && currentSelection().length >= q.max) return; // enforce max
          o.classList.toggle('is-selected');
          o.setAttribute('aria-checked', o.classList.contains('is-selected'));
          refreshMulti();
        } else {
          opts.forEach(function (x) { x.classList.remove('is-selected'); x.setAttribute('aria-checked', 'false'); });
          o.classList.add('is-selected'); o.setAttribute('aria-checked', 'true');
          self.store.set(q.id, o.getAttribute('data-value'));
          // Choosing a branch appends that branch's questions to the flow.
          if (q.rebuild) self.rebuildFlow();
          // Auto-advance for a conversational feel.
          self.lockAndAdvance();
        }
      });
      // Roving keyboard support within the group.
      o.addEventListener('keydown', function (e) {
        var i = opts.indexOf(o);
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); (opts[i + 1] || opts[0]).focus(); }
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); (opts[i - 1] || opts[opts.length - 1]).focus(); }
      });
    });

    if (isMulti) refreshMulti();
    if (this.continueBtn) this.continueBtn.addEventListener('click', function () { self.next(); });
    if (skipBtn) skipBtn.addEventListener('click', function () { self.store.set(q.id, []); self.next(); });
  };

  // Prevent double-advance from auto-advance + Enter.
  Quiz.prototype.lockAndAdvance = function () {
    var self = this;
    if (this._advancing) return; this._advancing = true;
    var delay = prefersReducedMotion() ? 120 : 320;
    setTimeout(function () { self._advancing = false; self.next(); }, delay);
  };

  function findOpt(q, value) {
    var found = null;
    (q.options || []).forEach(function (o) { if (o.value === value) found = o; });
    return found || { value: value, label: value };
  }

  // -------- Priority ranking --------------------------------------------
  Quiz.prototype.renderRank = function (q) {
    var self = this;
    var concerns = this.store.get('concerns') || [];
    var order = (this.store.get('priority_order') || []).filter(function (id) {
      return concerns.some(function (c) { return c.value === id; });
    });
    // seed / repair order from selected concerns
    concerns.forEach(function (c) { if (order.indexOf(c.value) === -1) order.push(c.value); });

    var panel = elFromHTML('<section class="sq-panel"></section>');
    var head =
      '<div class="sq-q__head sq-anim">' +
        '<p class="sq-eyebrow">' + esc(q.group) + '</p>' +
        '<h2 class="sq-h2">' + esc(q.title) + '</h2>' +
        '<p class="sq-lede sq-q__sub">' + esc(q.subtitle) + '</p>' +
      '</div>';
    panel.innerHTML = head + '<div class="sq-rank sq-anim" data-sq-rank></div>' +
      '<div class="sq-footer sq-anim"><button type="button" class="sq-btn sq-btn--primary" data-sq-continue>Continue ' + ICONS.arrow + '</button></div>';

    var listEl = panel.querySelector('[data-sq-rank]');
    function labelFor(id) { return (CONCERN_BY_ID[id] || {}).label || id; }

    function paint() {
      listEl.innerHTML = '';
      order.forEach(function (id, i) {
        var item = elFromHTML(
          '<div class="sq-rank__item' + self.scopeCls + '" draggable="true" tabindex="0" data-id="' + esc(id) + '" aria-label="' + esc(labelFor(id)) + ', position ' + (i + 1) + ' of ' + order.length + '">' +
            '<span class="sq-rank__pos">' + (i + 1) + '</span>' +
            '<span class="sq-rank__label">' + esc(labelFor(id)) + '</span>' +
            '<span class="sq-rank__btns">' +
              '<button type="button" data-up aria-label="Move up"' + (i === 0 ? ' disabled' : '') + '>' + ICONS.up + '</button>' +
              '<button type="button" data-down aria-label="Move down"' + (i === order.length - 1 ? ' disabled' : '') + '>' + ICONS.down + '</button>' +
            '</span>' +
            '<span class="sq-rank__grip" aria-hidden="true">' + ICONS.grip + '</span>' +
          '</div>');
        listEl.appendChild(item);
      });
      persist();
    }
    function move(id, delta) {
      var i = order.indexOf(id); var j = i + delta;
      if (j < 0 || j >= order.length) return;
      order.splice(j, 0, order.splice(i, 1)[0]);
      paint();
    }
    function persist() {
      self.store.set('priority_order', order.slice());
      self.store.set('priority', order[0]);
    }

    // click (arrows) + keyboard + drag
    listEl.addEventListener('click', function (e) {
      var up = e.target.closest('[data-up]'); var dn = e.target.closest('[data-down]');
      var row = e.target.closest('[data-id]'); if (!row) return;
      if (up) move(row.getAttribute('data-id'), -1);
      else if (dn) move(row.getAttribute('data-id'), 1);
    });
    listEl.addEventListener('keydown', function (e) {
      var row = e.target.closest('[data-id]'); if (!row) return;
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && (e.altKey || e.metaKey || e.ctrlKey)) {
        e.preventDefault(); move(row.getAttribute('data-id'), e.key === 'ArrowUp' ? -1 : 1);
        setTimeout(function () { var r = listEl.querySelector('[data-id="' + row.getAttribute('data-id') + '"]'); if (r) r.focus(); }, 0);
      }
    });
    // Drag & drop
    var dragId = null;
    listEl.addEventListener('dragstart', function (e) { var r = e.target.closest('[data-id]'); if (!r) return; dragId = r.getAttribute('data-id'); r.classList.add('is-dragging'); });
    listEl.addEventListener('dragend', function (e) { var r = e.target.closest('[data-id]'); if (r) r.classList.remove('is-dragging'); listEl.querySelectorAll('.is-over').forEach(function (x) { x.classList.remove('is-over'); }); });
    listEl.addEventListener('dragover', function (e) { e.preventDefault(); var r = e.target.closest('[data-id]'); listEl.querySelectorAll('.is-over').forEach(function (x) { x.classList.remove('is-over'); }); if (r && r.getAttribute('data-id') !== dragId) r.classList.add('is-over'); });
    listEl.addEventListener('drop', function (e) {
      e.preventDefault(); var r = e.target.closest('[data-id]'); if (!r || !dragId) return;
      var targetId = r.getAttribute('data-id'); if (targetId === dragId) return;
      var from = order.indexOf(dragId), to = order.indexOf(targetId);
      order.splice(to, 0, order.splice(from, 1)[0]); dragId = null; paint();
    });

    paint();
    this.continueBtn = panel.querySelector('[data-sq-continue]');
    this.continueBtn.addEventListener('click', function () { self.next(); });
    return panel;
  };

  // -------- Lead-capture card -------------------------------------------
  Quiz.prototype.renderContact = function (q) {
    var self = this;
    var saved = this.store.get('contact') || {};
    var panel = elFromHTML('<section class="sq-panel"></section>');

    var head =
      '<div class="sq-q__head sq-anim">' +
        '<p class="sq-eyebrow">' + esc(q.group) + '</p>' +
        '<h2 class="sq-h2">' + esc(q.title) + '</h2>' +
        (q.subtitle ? '<p class="sq-lede sq-q__sub">' + esc(q.subtitle) + '</p>' : '') +
      '</div>';

    function field(name, label, type, val, opts) {
      opts = opts || {};
      var id = 'sq-' + name;
      return '<label class="sq-field' + (opts.half ? ' sq-field--half' : '') + '">' +
          '<span class="sq-field__label">' + esc(label) + (opts.required ? ' <em aria-hidden="true">*</em>' : '') + '</span>' +
          '<input class="sq-input" id="' + id + '" name="' + name + '" type="' + type + '" ' +
            'value="' + esc(val || '') + '" ' +
            (opts.required ? 'required ' : '') +
            (opts.autocomplete ? 'autocomplete="' + opts.autocomplete + '" ' : '') +
            (type === 'tel' ? 'inputmode="tel" ' : '') +
            'data-sq-input>' +
          '<span class="sq-field__err" data-err></span>' +
        '</label>';
    }

    var locations = q.locations || [];
    var locHtml = locations.map(function (loc, i) {
      var checked = saved.location === loc;
      return '<button type="button" class="sq-opt sq-opt--compact' + self.scopeCls + (checked ? ' is-selected' : '') +
          '" role="radio" aria-checked="' + checked + '" data-loc="' + esc(loc) + '">' +
          '<span class="sq-opt__body"><span class="sq-opt__title">' + esc(loc) + '</span></span>' +
          '<span class="sq-opt__mark">' + ICONS.check + '</span>' +
        '</button>';
    }).join('');

    // Gender (about the person) — optional profile field, stored as a tag.
    var genderOpts = q.genderOptions || [];
    var genderHtml = genderOpts.map(function (g) {
      var val = g.value || g; var label = g.label || g;
      var checked = saved.gender === val;
      return '<button type="button" class="sq-chip' + self.scopeCls + (checked ? ' is-selected' : '') +
        '" role="radio" aria-checked="' + checked + '" data-gender="' + esc(val) + '">' + esc(label) +
        '<span class="sq-chip__x">' + ICONS.check + '</span></button>';
    }).join('');
    var genderField = (q.genderLabel && genderOpts.length)
      ? '<div class="sq-field"><span class="sq-field__label">' + esc(q.genderLabel) + (q.genderRequired ? ' <em aria-hidden="true">*</em>' : '') + '</span>' +
        '<div class="sq-options sq-options--chips" role="radiogroup" aria-label="' + esc(q.genderLabel) + '" data-sq-genders>' + genderHtml + '</div></div>'
      : '';

    var consent = q.consentLabel
      ? '<label class="sq-check"><input type="checkbox" data-sq-consent ' + (saved.consent != null ? (saved.consent ? 'checked' : '') : (q.consentDefault ? 'checked' : '')) + '>' +
          '<span class="sq-check__box">' + ICONS.check + '</span>' +
          '<span>' + esc(q.consentLabel) + '</span></label>'
      : '';

    var form =
      '<div class="sq-form sq-anim">' +
        '<div class="sq-form__row">' +
          field('firstName', 'First name', 'text', saved.firstName, { required: true, half: true, autocomplete: 'given-name' }) +
          field('lastName', 'Last name', 'text', saved.lastName, { half: true, autocomplete: 'family-name' }) +
        '</div>' +
        field('email', 'Email', 'email', saved.email, { required: true, autocomplete: 'email' }) +
        field('phone', 'Phone', 'tel', saved.phone, { required: q.requirePhone, autocomplete: 'tel' }) +
        genderField +
        (locations.length
          ? '<div class="sq-field"><span class="sq-field__label">Preferred location <em aria-hidden="true">*</em></span>' +
            '<div class="sq-locations" role="radiogroup" aria-label="Preferred location" data-sq-locations>' + locHtml + '</div>' +
            '<span class="sq-field__err" data-loc-err></span></div>'
          : '') +
        consent +
        '<p class="sq-fineprint">We use your details to build and save your routine and connect you with your chosen clinic. We’ll never share them.</p>' +
      '</div>';

    panel.innerHTML = head + form +
      '<div class="sq-footer sq-anim"><button type="button" class="sq-btn sq-btn--primary" data-sq-continue disabled>Continue ' + ICONS.arrow + '</button></div>';

    this.wireContact(panel, q);
    return panel;
  };

  Quiz.prototype.wireContact = function (panel, q) {
    var self = this;
    var inputs = Array.prototype.slice.call(panel.querySelectorAll('[data-sq-input]'));
    var locBtns = Array.prototype.slice.call(panel.querySelectorAll('[data-loc]'));
    var consentEl = panel.querySelector('[data-sq-consent]');
    this.continueBtn = panel.querySelector('[data-sq-continue]');
    var locErr = panel.querySelector('[data-loc-err]');

    var genderBtns = Array.prototype.slice.call(panel.querySelectorAll('[data-gender]'));
    function get(name) { var el = panel.querySelector('[name="' + name + '"]'); return el ? el.value.trim() : ''; }
    function selectedLoc() { var b = panel.querySelector('[data-loc].is-selected'); return b ? b.getAttribute('data-loc') : ''; }
    function selectedGender() { var b = panel.querySelector('[data-gender].is-selected'); return b ? b.getAttribute('data-gender') : ''; }
    function emailValid(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

    function collect() {
      return {
        firstName: get('firstName'), lastName: get('lastName'),
        email: get('email'), phone: get('phone'),
        gender: selectedGender(),
        location: selectedLoc(),
        consent: consentEl ? consentEl.checked : false
      };
    }
    function valid(c) {
      if (!c.firstName) return false;
      if (!emailValid(c.email)) return false;
      if (q.locations && q.locations.length && !c.location) return false;
      if (q.genderRequired && !c.gender) return false;
      if (q.requirePhone && !c.phone) return false;
      return true;
    }
    function sync() {
      var c = collect();
      self.store.set('contact', c);
      self.continueBtn.disabled = !valid(c);
    }

    inputs.forEach(function (el) {
      el.addEventListener('input', sync);
      el.addEventListener('blur', function () {
        var err = el.parentNode.querySelector('[data-err]');
        if (!err) return;
        if (el.name === 'email' && el.value && !emailValid(el.value)) err.textContent = 'Enter a valid email.';
        else if (el.required && !el.value.trim()) err.textContent = 'Required.';
        else err.textContent = '';
      });
    });
    locBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        locBtns.forEach(function (x) { x.classList.remove('is-selected'); x.setAttribute('aria-checked', 'false'); });
        b.classList.add('is-selected'); b.setAttribute('aria-checked', 'true');
        if (locErr) locErr.textContent = '';
        sync();
      });
    });
    genderBtns.forEach(function (b) {
      b.addEventListener('click', function () {
        var was = b.classList.contains('is-selected');
        genderBtns.forEach(function (x) { x.classList.remove('is-selected'); x.setAttribute('aria-checked', 'false'); });
        if (!was) { b.classList.add('is-selected'); b.setAttribute('aria-checked', 'true'); } // allow deselect (optional)
        sync();
      });
    });
    if (consentEl) consentEl.addEventListener('change', sync);

    this.continueBtn.addEventListener('click', function () {
      var c = collect();
      if (!valid(c)) { sync(); return; }
      self.store.set('contact', c);
      self.dispatch('lead', { contact: c });
      if (self.cfg.lead && (self.cfg.lead.capture === 'lead' || self.cfg.lead.capture === 'both')) {
        self.submitCustomer(self.store.state.answers, 'lead');
      }
      self.next();
    });

    sync();
  };

  // -------- Finish: analyze then results --------------------------------
  Quiz.prototype.finish = function () {
    var self = this;
    this.store.setMeta({ completed: true });
    this.refs.progress.hidden = true;

    var messages = [
      'Analyzing your skin profile…',
      'Matching clinician-grade formulas…',
      'Balancing your morning and evening steps…',
      'Finalizing your personalized routine…'
    ];
    var panel = elFromHTML(
      '<section class="sq-panel"><div class="sq-analyze sq-anim">' +
        '<div class="sq-analyze__ring" aria-hidden="true"></div>' +
        '<h2 class="sq-h2">' + esc(this.cfg.copy.analyzeHeading) + '</h2>' +
        '<p class="sq-analyze__status" data-sq-status aria-live="polite">' + esc(messages[0]) + '</p>' +
      '</div></section>');
    this.swapPanel(panel, 'forward');

    var status = panel.querySelector('[data-sq-status]');
    var i = 0;
    var total = Math.max(0, this.cfg.analyzeMs | 0);
    var per = total / messages.length;
    var timer = setInterval(function () {
      i++;
      if (i < messages.length && status) { status.style.opacity = '0'; setTimeout(function () { status.textContent = messages[i]; status.style.opacity = ''; }, 180); }
    }, per || 600);

    // Build the routine from the render-time catalogue (Shopify's own truth).
    // We do NOT pre-verify availability over the network here: it added 8
    // requests per result view and risked mis-flagging in-stock products.
    // Instead, availability is resolved lazily per-product when an add fails.
    var answers = this.store.state.answers;
    var routine = this.engine.recommend(answers);
    this.routine = routine;
    var tags = this.buildCustomerTags(answers, routine);
    this.dispatch('complete', { answers: answers, contact: answers.contact || null, tags: tags, routine: serializeRoutine(routine) });

    // Create / update the Shopify customer with their details + quiz tags.
    if (this.cfg.lead && this.cfg.lead.enabled && this.cfg.lead.capture !== 'lead') {
      this.submitCustomer(answers, 'complete', routine);
    }

    setTimeout(function () {
      clearInterval(timer);
      self.renderResults(routine);
    }, total);
  };

  /* ======================================================================
   * [7b] CUSTOMER RECORD (native Shopify customer form — no app)
   *   Builds tags + a staff note from the answers and submits Shopify's
   *   built-in `{% form 'customer' %}`, creating/updating an email-subscriber
   *   customer with the quiz data. Commas are stripped from tags (the field is
   *   comma-delimited). Fire-and-forget: never blocks the results UI.
   * ==================================================================== */

  function sanitizeTag(t) { return String(t == null ? '' : t).replace(/,/g, ' ').replace(/\s+/g, ' ').trim(); }
  function answerIdList(a) { return (a || []).map(function (x) { return (x && typeof x === 'object') ? x.value : x; }).filter(Boolean); }

  // Branch-specific goal labels for tagging (body/hair/wellness).
  function branchGoalLabels(answers) {
    var branch = optVal(answers.branch);
    var key = branch === 'body' ? 'body_concerns' : branch === 'hair' ? 'hair_goal' : branch === 'wellness' ? 'wellness_goals' : null;
    if (!key) return [];
    return optIds(answers[key]).map(function (id) { return (BRANCH_CONCERN_BY_ID[id] || {}).label; }).filter(Boolean);
  }

  Quiz.prototype.buildCustomerTags = function (answers, routine) {
    var tags = ['Skin Quiz'];
    var c = answers.contact || {};
    var branch = optVal(answers.branch) || 'skin';
    if (c.location) tags.push('Location: ' + c.location);
    if (c.gender) tags.push('Gender: ' + c.gender);
    tags.push('Interest: ' + ((BRANCHES[branch] || {}).label || branch));

    if (branch === 'skin') {
      var AGE = { under25: 'Under 25', '25_34': '25-34', '35_44': '35-44', '45_54': '45-54', '55plus': '55+' };
      var SENS = { low: 'Not Sensitive', some: 'Somewhat Sensitive', high: 'Very Sensitive' };
      var EXP = { new: 'New to Skincare', some: 'Comfortable', advanced: 'Advanced' };
      var RET = { never: 'Never', previously: 'Previously', currently: 'Currently' };
      if (AGE[answers.age]) tags.push('Age: ' + AGE[answers.age]);
      var skin = SKIN_BY_ID[answers.skintype]; if (skin) tags.push('Skin Type: ' + skin.label);
      if (SENS[answers.sensitivity]) tags.push('Sensitivity: ' + SENS[answers.sensitivity]);
      if (EXP[answers.experience]) tags.push('Experience: ' + EXP[answers.experience]);
      answerIdList(answers.concerns).forEach(function (cid) { var cc = CONCERN_BY_ID[cid]; if (cc) tags.push('Concern: ' + cc.label); });
      var pri = CONCERN_BY_ID[optVal(answers.priority)];
      if (pri) tags.push('Priority: ' + pri.label);
      if (RET[answers.retinol]) tags.push('Retinol: ' + RET[answers.retinol]);
    } else {
      branchGoalLabels(answers).forEach(function (l) { tags.push('Goal: ' + l); });
    }
    (answers.preferences || []).concat(answers.wellness_diet || []).forEach(function (p) { if (p && p.label) tags.push('Pref: ' + p.label); });
    if (c.consent) tags.push('Email Opt-In');

    // De-dupe + sanitize.
    var seen = {}, out = [];
    tags.forEach(function (t) { t = sanitizeTag(t); if (t && !seen[t.toLowerCase()]) { seen[t.toLowerCase()] = 1; out.push(t); } });
    return out;
  };

  Quiz.prototype.buildCustomerNote = function (answers, routine) {
    var branch = optVal(answers.branch) || 'skin';
    var lines = [((BRANCHES[branch] || {}).label || 'Skincare') + ' consultation.'];
    var c = answers.contact || {};
    if (c.location) lines.push('Preferred clinic: ' + c.location);
    if (branch === 'skin') {
      var skin = SKIN_BY_ID[answers.skintype];
      if (skin) lines.push('Skin type: ' + skin.label);
      var concerns = answerIdList(answers.concerns).map(function (cid) { return (CONCERN_BY_ID[cid] || {}).label; }).filter(Boolean);
      if (concerns.length) lines.push('Concerns: ' + concerns.join(', '));
    } else {
      var goals = branchGoalLabels(answers);
      if (goals.length) lines.push('Goals: ' + goals.join(', '));
    }
    if (routine) {
      var names = (routine.all || []).map(function (x) { return x.step + ': ' + x.product.title; });
      if (names.length) lines.push('Recommended — ' + names.join('; '));
    }
    return lines.join('\n');
  };

  /** Submit Shopify's native customer form with the captured details + tags. */
  Quiz.prototype.submitCustomer = function (answers, phase, routine) {
    var self = this;
    var form = this.refs.customerForm;
    var c = (answers && answers.contact) || {};
    if (!form || !c.email) return Promise.resolve(false);
    // Only submit once per phase (avoids duplicate writes on re-render/back).
    this._customerSubmitted = this._customerSubmitted || {};
    if (this._customerSubmitted[phase]) return Promise.resolve(false);
    this._customerSubmitted[phase] = true;

    function set(name, val) { var el = form.querySelector('[name="' + name + '"]'); if (el) el.value = (val == null ? '' : val); }
    set('contact[first_name]', c.firstName);
    set('contact[last_name]', c.lastName);
    set('contact[email]', c.email);
    set('contact[phone]', c.phone);
    set('contact[tags]', this.buildCustomerTags(answers, routine).join(','));
    set('contact[note]', this.buildCustomerNote(answers, routine));
    set('contact[accepts_marketing]', c.consent ? 'true' : 'false');

    return fetch(form.action, { method: 'POST', body: new FormData(form), headers: { 'Accept': 'text/html' } })
      .then(function () { self.dispatch('customercreated', { email: c.email, phase: phase }); return true; })
      .catch(function () { return false; }); // never block the experience
  };

  Quiz.prototype.renderResults = function (routine) {
    var self = this;
    var c = this.cfg.copy;
    var profile = routine.profile;

    var summary = this.summaryTags(profile);
    var totalCount = routine.all.length;

    var panel = elFromHTML('<section class="sq-panel sq-results"></section>');
    var html =
      '<div class="sq-results__head sq-anim">' +
        '<p class="sq-eyebrow">Your consultation</p>' +
        '<h2 class="sq-h2">' + esc(c.resultsHeading) + '</h2>' +
        '<p class="sq-lede" style="margin-inline:auto">' + esc(c.resultsSubheading) + '</p>' +
        '<div class="sq-results__summary">' + summary + '</div>' +
      '</div>' +
      '<div class="sq-routinebar sq-anim' + this.scopeCls + '" data-sq-routinebar>' +
        '<div class="sq-routinebar__meta"><strong>Your routine</strong><span data-sq-routinemeta>' + totalCount + ' products</span></div>' +
        '<button type="button" class="sq-btn sq-btn--ghost" data-sq-restart-inline>Retake</button>' +
        '<button type="button" class="sq-btn sq-btn--primary" data-sq-addall>' + ICONS.plus + ' ' + esc(c.addAll) + '</button>' +
      '</div>';

    if (routine.branch === 'skin') {
      html += this.routineMarkup('Your Routine', ICONS.sun, 'Each product once — with when to use it.', routine.core);
      if (routine.enhancements && routine.enhancements.length) {
        html += this.routineMarkup('Optional Enhancements', ICONS.spark, 'Considered additions to elevate your results.', routine.enhancements);
      }
    } else {
      // Body / Hair / Wellness — one section per step group.
      var groupIcon = { body: ICONS.body, hair: ICONS.hair, wellness: ICONS.leaf }[routine.branch] || ICONS.spark;
      (routine.groups || []).forEach(function (g) {
        html += self.routineMarkup(g.label, groupIcon, '', g.cards);
      });
    }

    // Consultation CTA — carries its own theme color scheme.
    var cs = this.cfg.consultation;
    html +=
      '<div class="sq-consult sq-anim' + this.consultScopeCls + '">' +
        '<h2 class="sq-h2">' + esc(cs.heading) + '</h2>' +
        '<p>' + esc(cs.body) + '</p>' +
        '<div class="sq-consult__actions">' +
          '<a class="sq-btn sq-btn--primary sq-btn--lg" href="' + esc(cs.url || '#') + '"' + (cs.url && cs.url !== '#' ? '' : ' data-sq-noop') + '>' + esc(cs.label) + '</a>' +
          (cs.secondaryUrl ? '<a class="sq-btn sq-btn--ghost" href="' + esc(cs.secondaryUrl) + '">' + esc(cs.secondaryLabel) + '</a>' : '') +
        '</div>' +
      '</div>' +
      '<button type="button" class="sq-restart" data-sq-restart>Start over</button>';

    panel.innerHTML = html;
    this.swapPanel(panel, 'forward');
    this.wireResults(panel, routine);
  };

  Quiz.prototype.summaryTags = function (profile) {
    var tags = [];
    if (profile.branch && profile.branch !== 'skin') {
      tags.push(['Focus', (BRANCHES[profile.branch] || {}).label || '']);
      (profile.selectedLabels || []).slice(0, 3).forEach(function (l) { tags.push(['', l]); });
    } else {
      var skin = SKIN_BY_ID[profile.skintype];
      if (skin) tags.push(['Skin', skin.label]);
      if (profile.sensitivity === 'high') tags.push(['Sensitivity', 'High']);
      var pr = CONCERN_BY_ID[profile.priority];
      if (pr) tags.push(['Priority', pr.label]);
      if ((profile.concerns || []).length > 1) tags.push(['Focus', profile.concerns.length + ' concerns']);
      if ((profile.prefs || []).length) tags.push(['Preferences', profile.prefs.length + '']);
    }
    return tags.map(function (t) {
      return '<span class="sq-tag">' + (t[0] ? esc(t[0]) + ' · ' : '') + '<strong>' + esc(t[1]) + '</strong></span>';
    }).join('');
  };

  Quiz.prototype.routineMarkup = function (title, icon, desc, cards) {
    if (!cards.length) return '';
    var self = this;
    var body = cards.map(function (card) { return self.cardMarkup(card); }).join('');
    return '<section class="sq-routine sq-anim">' +
        '<div class="sq-routine__head">' +
          '<span class="sq-routine__icon">' + icon + '</span>' +
          '<h3 class="sq-routine__title">' + esc(title) + '</h3>' +
          '<p class="sq-routine__desc">' + esc(desc) + '</p>' +
        '</div>' +
        '<div class="sq-cards">' + body + '</div>' +
      '</section>';
  };

  // Client-rendered card — mirrors snippets/product-card-quiz.liquid.
  Quiz.prototype.cardMarkup = function (card) {
    var p = card.product;
    var benefits = parseBenefits(p.benefits);
    var media = p.image
      ? '<img src="' + esc(p.image) + '" alt="' + esc(p.imageAlt) + '" loading="lazy" width="640" height="640">'
      : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';

    var price = '<ins>' + esc(p.price) + '</ins>' + (p.compareAt ? '<del>' + esc(p.compareAt) + '</del>' : '');

    var details = '';
    if (benefits.length) details += this.detailMarkup('Benefits', '<ul>' + benefits.map(function (b) { return '<li>' + esc(b) + '</li>'; }).join('') + '</ul>');
    if (p.keyingredient.length) details += this.detailMarkup('Key ingredients', '<div class="sq-chips-ingredients">' + p.keyingredient.slice(0, 8).map(function (i) { return '<span>' + esc(i) + '</span>'; }).join('') + '</div>');
    if (p.howtouse) details += this.detailMarkup('How to use', '<p>' + esc(p.howtouse) + '</p>');

    var foot = p.available && p.variantId
      ? '<button type="button" class="sq-add" data-sq-add data-variant="' + esc(p.variantId) + '">' +
          '<span class="sq-add__idle">' + ICONS.plus + ' Add to routine</span>' +
          '<span class="sq-add__done">' + ICONS.check + ' Added</span>' +
        '</button>'
      : '<button type="button" class="sq-add" disabled>Currently unavailable</button>';

    // Time-of-day badge (skin routine): each product shown once, labelled when.
    var whenLabel = { am: 'Morning', pm: 'Evening', both: 'AM & PM' }[card.when];
    var whenBadge = whenLabel
      ? '<span class="sq-card__when' + (card.when === 'both' ? ' is-both' : '') + '">' +
          (card.when === 'am' ? ICONS.sun : card.when === 'pm' ? ICONS.moon : ICONS.sun + ICONS.moon) +
          whenLabel + '</span>'
      : '';

    return '<article class="sq-card' + this.scopeCls + '" data-product-id="' + esc(p.id) + '">' +
        '<div class="sq-card__media' + (p.image ? '' : ' sq-card__media--empty') + '">' +
          '<span class="sq-card__stepflag">' + esc(card.step) + '</span>' + whenBadge + media +
        '</div>' +
        '<div class="sq-card__body">' +
          (p.vendor ? '<p class="sq-card__brand">' + esc(p.vendor) + '</p>' : '') +
          '<h4 class="sq-card__title"><a href="' + esc(p.url) + '">' + esc(p.title) + '</a></h4>' +
          '<div class="sq-card__price">' + price + '</div>' +
          (card.why ? '<div class="sq-why"><span class="sq-why__label">' + ICONS.spark + ' Why we chose it</span><p class="sq-why__text">' + esc(card.why) + '</p></div>' : '') +
          details +
          '<div class="sq-card__foot">' + foot + '</div>' +
        '</div>' +
      '</article>';
  };

  Quiz.prototype.detailMarkup = function (label, inner) {
    return '<div class="sq-detail" data-sq-detail>' +
        '<button type="button" class="sq-detail__sum" aria-expanded="false">' + esc(label) + ICONS.chevron + '</button>' +
        '<div class="sq-detail__panel"><div class="sq-detail__inner"><div class="sq-detail__content">' + inner + '</div></div></div>' +
      '</div>';
  };

  Quiz.prototype.wireResults = function (panel, routine) {
    var self = this;

    // Expandable details
    panel.addEventListener('click', function (e) {
      var sum = e.target.closest('.sq-detail__sum');
      if (sum) {
        var d = sum.closest('.sq-detail');
        var open = d.classList.toggle('is-open');
        sum.setAttribute('aria-expanded', open);
        return;
      }
      var add = e.target.closest('[data-sq-add]');
      if (add) { self.addSingle(add); return; }
    });

    var addAll = panel.querySelector('[data-sq-addall]');
    if (addAll) addAll.addEventListener('click', function () { self.addAll(routine, addAll); });

    [panel.querySelector('[data-sq-restart]'), panel.querySelector('[data-sq-restart-inline]')].forEach(function (b) {
      if (b) b.addEventListener('click', function () { self.restart(); });
    });

    var noop = panel.querySelector('[data-sq-noop]');
    if (noop) noop.addEventListener('click', function (e) { e.preventDefault(); self.toast('Add your booking link in the theme editor.'); });
  };

  Quiz.prototype.restart = function () {
    this.store.reset();
    this._customerSubmitted = {}; // a fresh run may submit again
    this.index = -1;
    if (this.active && this.active.parentNode) this.active.parentNode.removeChild(this.active);
    this.active = null;
    this.refs.progress.hidden = true;
    this.refs.welcome.hidden = false;
    this.refs.fill.style.width = '0%';
    this.animateIn(this.refs.welcome);
    this.scrollTop();
  };

  /* ======================================================================
   * [8] CART INTEGRATION
   * ==================================================================== */

  // Shopify's 422 payload: { status, message, description }.
  function cartErrorText(err) {
    if (err && typeof err.description === 'string' && err.description) return err.description;
    if (err && typeof err.message === 'string' && err.message !== 'Cart Error') return err.message;
    return '';
  }

  Quiz.prototype.productById = function (id) {
    var found = null;
    this.engine.products.forEach(function (x) { if (String(x.id) === String(id)) found = x; });
    return found;
  };

  /** Mark ONE product sold out: flag it in the engine (so a retake re-picks),
   *  disable its card button, and refresh the routine-bar count. Never mass-
   *  applies — the only path here is an explicit per-product signal.          */
  Quiz.prototype.markSoldOut = function (productId) {
    var p = this.productById(productId);
    if (p) p.available = false;
    this.root.querySelectorAll('.sq-card[data-product-id="' + productId + '"]').forEach(function (card) {
      card.classList.remove('is-added');
      var add = card.querySelector('[data-sq-add]');
      if (add) { add.classList.remove('is-loading', 'is-added'); add.disabled = true; add.textContent = 'Sold out'; }
    });
    if (this.routine) {
      var left = this.routine.all.filter(function (c) { return c.product.available && c.product.variantId; }).length;
      var meta = this.root.querySelector('[data-sq-routinemeta]');
      if (meta) meta.textContent = left + ' product' + (left === 1 ? '' : 's');
    }
  };

  /** Lazily fetch a product's CURRENT variant from the storefront — used only
   *  to recover from a failed add (stale cached page, or genuine sell-out).
   *  Resolves { available, variantId } or null if we can't determine.         */
  Quiz.prototype.resolveLiveVariant = function (p) {
    if (!p || !p.handle) return Promise.resolve(null);
    return fetch('/products/' + encodeURIComponent(p.handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !data.variants || !data.variants.length) return null;
        var current = null, firstAvail = null;
        data.variants.forEach(function (v) {
          if (v.id === p.variantId) current = v;
          if (!firstAvail && v.available) firstAvail = v;
        });
        if (current && current.available) return { available: true, variantId: current.id };
        if (firstAvail) return { available: true, variantId: firstAvail.id };
        return { available: false, variantId: p.variantId };
      })
      .catch(function () { return null; });
  };

  Quiz.prototype.addSingle = function (btn) {
    var self = this;
    var id = parseInt(btn.getAttribute('data-variant'), 10);
    if (!id) return;
    var card = btn.closest('.sq-card');
    var pid = card && card.getAttribute('data-product-id');
    var p = pid && self.productById(pid);

    function succeed(addedId) {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      if (card) card.classList.add('is-added');
      self.toast('Added to cart', true);
      self.dispatch('addtocart', { items: [addedId] });
    }
    function fail(err) {
      btn.classList.remove('is-loading');
      self.toast(cartErrorText(err) || 'Sorry — could not add that item.');
    }

    btn.classList.add('is-loading');
    this.cartAdd([{ id: id, quantity: 1 }])
      .then(function () { succeed(id); })
      .catch(function (err) {
        // The add failed — resolve this product's CURRENT variant and retry
        // once. Handles a stale cached page (variant re-created) transparently,
        // and only marks THIS product sold out if it truly has no stock.
        if (!p) return fail(err);
        self.resolveLiveVariant(p).then(function (live) {
          if (live && live.available && live.variantId !== id) {
            p.variantId = live.variantId;
            btn.setAttribute('data-variant', live.variantId);
            self.cartAdd([{ id: live.variantId, quantity: 1 }])
              .then(function () { succeed(live.variantId); })
              .catch(fail);
          } else if (live && !live.available) {
            self.markSoldOut(pid);
            fail(err);
          } else {
            fail(err);
          }
        });
      });
  };

  Quiz.prototype.addAll = function (routine, btn) {
    var self = this;
    var cards = routine.all.filter(function (c) { return c.product.available && c.product.variantId; });
    if (!cards.length) { this.toast('No available products to add.'); return; }
    var original = btn.innerHTML;
    btn.disabled = true; btn.innerHTML = 'Adding your routine…';

    // Add items one at a time so a single stale/sold-out product can't fail
    // the whole routine. Each add self-heals via addOneResilient().
    var added = [], failed = [];
    var chain = Promise.resolve();
    cards.forEach(function (c) {
      chain = chain.then(function () {
        return self.addOneResilient(c.product).then(function (ok) {
          if (ok) { added.push(c.product.id); self.reflectAdded(c.product.id); }
          else { failed.push(c.product); self.markSoldOut(c.product.id); }
        });
      });
    });

    chain.then(function () {
      btn.disabled = false;
      if (added.length) {
        btn.innerHTML = ICONS.check + ' Routine added';
        self.afterCart(); // one refresh/drawer-open for the whole batch
        var msg = added.length + ' product' + (added.length === 1 ? '' : 's') + ' added to your cart';
        if (failed.length) msg += ' · ' + failed.length + ' unavailable';
        self.toast(msg, true);
        self.dispatch('addtocart', { items: added, all: true });
      } else {
        btn.innerHTML = original;
        self.toast('Sorry — those products are currently unavailable.');
      }
    });
  };

  // Reflect an added product on its card button(s).
  Quiz.prototype.reflectAdded = function (productId) {
    this.root.querySelectorAll('.sq-card[data-product-id="' + productId + '"] [data-sq-add]').forEach(function (a) {
      a.classList.remove('is-loading'); a.classList.add('is-added');
      var card = a.closest('.sq-card'); if (card) card.classList.add('is-added');
    });
  };

  /** Add a single product, self-healing a stale variant id. Resolves true on
   *  success, false if the product is genuinely unavailable.                  */
  Quiz.prototype.addOneResilient = function (p) {
    var self = this;
    if (!p.variantId) return Promise.resolve(false);
    return this.cartAdd([{ id: p.variantId, quantity: 1 }], true)
      .then(function () { return true; })
      .catch(function () {
        return self.resolveLiveVariant(p).then(function (live) {
          if (live && live.available && live.variantId !== p.variantId) {
            p.variantId = live.variantId;
            return self.cartAdd([{ id: live.variantId, quantity: 1 }], true).then(function () { return true; }).catch(function () { return false; });
          }
          return false;
        });
      });
  };

  // POST to Shopify's AJAX Cart API. Pass silent:true to suppress the theme
  // refresh (used when batching adds — afterCart() runs once at the end).
  Quiz.prototype.cartAdd = function (items, silent) {
    var self = this;
    var url = (this.cfg.routesCartAdd || '/cart/add') + '.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (res) {
      if (!res.ok) return res.json().then(function (e) { throw e; });
      return res.json();
    }).then(function (data) {
      if (!silent) self.afterCart();
      return data;
    });
  };

  // Reflect the new cart in the host theme. Horizon components communicate via
  // a bubbling `cart:update` event (see assets/events.js): the header cart icon
  // reads detail.data.itemCount, cart-items components re-render their section,
  // and the cart drawer auto-opens when it has the `auto-open` attribute.
  Quiz.prototype.afterCart = function () {
    var behavior = this.cfg.cartBehavior || 'drawer';
    if (behavior === 'redirect') { window.location = this.cfg.routesCart || '/cart'; return; }

    fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
      try {
        // Horizon (assets/events.js CartAddEvent shape)
        var evt = new Event('cart:update', { bubbles: true });
        evt.detail = {
          resource: cart,
          sourceId: 'skin-quiz',
          data: { source: 'skin-quiz', itemCount: cart.item_count }
        };
        document.dispatchEvent(evt);

        // Generic fallbacks for non-Horizon themes.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span, #CartCount').forEach(function (el) {
          if (el) el.textContent = cart.item_count;
        });

        if (behavior === 'drawer') {
          // Horizon drawer first, then Dawn-style as a fallback.
          var drawer = document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }
      } catch (e) { /* non-fatal */ }
    }).catch(function () {});
  };

  Quiz.prototype.toast = function (msg, withLink) {
    var t = this.refs.toast, txt = this.refs.toastText;
    if (!t) return;
    txt.innerHTML = esc(msg) + (withLink ? ' · <a href="' + esc(this.cfg.routesCart || '/cart') + '">View cart</a>' : '');
    t.hidden = false; void t.offsetWidth; t.classList.add('is-visible');
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () { t.classList.remove('is-visible'); setTimeout(function () { t.hidden = true; }, 400); }, 4200);
  };

  /* ======================================================================
   * [9] PUBLIC API + EXTENSION HOOKS
   *   Future modules (AI selfie analysis, clinic/provider targeting, seasonal
   *   logic, Klaviyo, customer accounts, "email my routine") integrate here
   *   without touching the core.
   * ==================================================================== */
  Quiz.prototype.dispatch = function (name, detail) {
    // (a) DOM event — for analytics, Klaviyo, GTM, custom listeners.
    this.root.dispatchEvent(new CustomEvent('skinquiz:' + name, { bubbles: true, detail: detail }));
    // (b) registered plugins — synchronous hooks.
    (SkinQuizAPI._hooks[name] || []).forEach(function (fn) { try { fn(detail); } catch (e) { console.error('[SkinQuiz hook]', e); } });
  };

  function serializeRoutine(routine) {
    function map(cards) { return (cards || []).map(function (c) { return { id: c.product.id, title: c.product.title, step: c.step, when: c.when || null, variantId: c.product.variantId, why: c.why }; }); }
    return { branch: routine.branch, items: map(routine.all), enhancements: map(routine.enhancements), profile: routine.profile };
  }

  var SkinQuizAPI = {
    _instances: [],
    _hooks: {},
    /** Register a hook: on('complete'|'start'|'addtocart', fn). */
    on: function (event, fn) { (this._hooks[event] = this._hooks[event] || []).push(fn); return this; },
    /** Register an extra ingredient rule (extend scoring + rationale). */
    addIngredientRule: function (rule) { if (rule && rule.kw && rule.targets) INGREDIENT_RULES.push(rule); return this; },
    /** Access the live quiz instance(s). */
    instances: function () { return this._instances; }
  };

  /* ======================================================================
   * [10] BOOT
   * ==================================================================== */
  function readJSON(root, selector) {
    var el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { console.error('[SkinQuiz] bad JSON in ' + selector, e); return null; }
  }

  function init(root) {
    if (root.__skinquiz) return;
    var products = readJSON(root, '[data-sq-products]') || [];
    var config = readJSON(root, '[data-sq-config]') || {};
    config.copy = config.copy || {};
    config.consultation = config.consultation || {};
    config.lead = config.lead || { enabled: false };
    if (config.lead.enabled && !(config.lead.locations && config.lead.locations.length)) config.lead.locations = [];
    if (config.maxConcerns == null) config.maxConcerns = 3;
    var quiz = new Quiz(root, { products: products, config: config });
    root.__skinquiz = quiz;
    SkinQuizAPI._instances.push(quiz);
  }

  function boot() {
    document.querySelectorAll('[data-skinquiz]').forEach(init);
  }

  // Expose the API globally for extensions.
  window.SkinQuiz = SkinQuizAPI;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-init when re-added by the theme editor (section reload).
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-skinquiz]') : null;
    if (root) init(root);
  });
})();
